import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stage, Layer, Rect, Circle, Line, Text, Group, Shape, Transformer, Image } from 'react-konva';
import useImage from 'use-image';
import './ClipEditor.css';
import { API_URL } from './Config';
import Konva from 'konva';
import arrowSvg from './assets/arrow.svg';
import VideoGenerationModal from './components/VideoGenerationModal';

Konva._fixTextRendering = true;

// Context Menu Component for Ripple Delete
const RippleDeleteContextMenu = ({ isVisible, x, y, onRippleDelete, onClose, gapDuration, clipsCount }) => {
  if (!isVisible) return null;

  return (
    <div 
      className="ripple-delete-context-menu"
      style={{ 
        position: 'fixed', 
        left: x, 
        top: y,
        zIndex: 10000,
        background: '#2a2a2a',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '8px 0',
        minWidth: '180px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }}
    >
      <div 
        className="context-menu-item"
        style={{
          padding: '8px 16px',
          cursor: 'pointer',
          color: '#fff',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
        onClick={onRippleDelete}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a3a'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 3V4H4V6H5V19C5 20.1 5.9 21 7 21H17C18.1 21 19 20.1 19 19V6H20V4H15V3H9ZM7 6H17V19H7V6Z"/>
        </svg>
        <div>
          <div>Ripple Delete</div>
          <div style={{ fontSize: '12px', color: '#ccc' }}>
            Close {gapDuration?.toFixed(2)}s gap, move {clipsCount} clip(s)
          </div>
        </div>
      </div>
    </div>
  );
};

// Layer Context Menu Component
const LayerContextMenu = ({ isVisible, x, y, layerType, onExtendToStart, onExtendToEnd, onDuplicate, onClose }) => {
  if (!isVisible) return null;

  return (
    <div
      className="layer-context-menu"
      style={{
        position: 'absolute',
        left: x,
        top: y,
        zIndex: 10000,
        background: '#2a2a2a',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '8px 0',
        minWidth: '160px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        color: '#fff',
        fontSize: '14px'
      }}
    >
      {/* Extend submenu */}
      <div style={{ padding: '8px 16px', color: '#ccc', fontSize: '12px', fontWeight: 'bold' }}>
        Extend
      </div>

      <div
        className="context-menu-item"
        style={{
          padding: '8px 24px',
          cursor: 'pointer',
          background: 'transparent'
        }}
        onMouseEnter={(e) => e.target.style.background = '#3a3a3a'}
        onMouseLeave={(e) => e.target.style.background = 'transparent'}
        onClick={onExtendToStart}
      >
        to Start
      </div>

      <div
        className="context-menu-item"
        style={{
          padding: '8px 24px',
          cursor: 'pointer',
          background: 'transparent'
        }}
        onMouseEnter={(e) => e.target.style.background = '#3a3a3a'}
        onMouseLeave={(e) => e.target.style.background = 'transparent'}
        onClick={onExtendToEnd}
      >
        to End
      </div>

      {/* Separator */}
      <div style={{ height: '1px', background: '#444', margin: '4px 8px' }}></div>

      {/* Duplicate option */}
      <div
        className="context-menu-item"
        style={{
          padding: '8px 16px',
          cursor: 'pointer',
          background: 'transparent'
        }}
        onMouseEnter={(e) => e.target.style.background = '#3a3a3a'}
        onMouseLeave={(e) => e.target.style.background = 'transparent'}
        onClick={onDuplicate}
      >
        Duplicate
      </div>
    </div>
  );
};

// Arrow Container Component - SVG Arrow with dynamic bounding rectangle
const ArrowContainer = ({ shape, opacity, handleShapeClick, handleShapeTransform, handleShapeDragEnd, shapeRefs }) => {
  const [image] = useImage(arrowSvg);
  
  // Calculate bounding box dimensions based on arrow rotation
  const angleRad = (shape.rotation || 0) * Math.PI / 180;
  const cos = Math.abs(Math.cos(angleRad));
  const sin = Math.abs(Math.sin(angleRad));
  const boundingWidth = Math.max(0, Math.abs(shape.width) * cos + Math.abs(shape.height) * sin);
  const boundingHeight = Math.max(0, Math.abs(shape.width) * sin + Math.abs(shape.height) * cos);
  
  return (
    <React.Fragment>
      {/* Dynamic bounding rectangle - resizes based on arrow rotation */}
      <Rect
        key={`${shape.id}-bounding`}
        x={shape.x - boundingWidth/2}
        y={shape.y - boundingHeight/2}
        width={boundingWidth}
        height={boundingHeight}
        fill="transparent"
        stroke="transparent"
        strokeWidth={0}
        listening={false} // Non-interactive, just visual
      />
      
      {/* SVG Arrow - this is the interactive element */}
      <Image
        key={`${shape.id}-arrow`}
        ref={node => {
          if (node) {
            shapeRefs.current[shape.id] = node; // Arrow is the interactive element
          }
        }}
        image={image}
        x={shape.x}
        y={shape.y}
        width={shape.width}
        height={shape.height}
        offsetX={shape.width / 2}  // Rotate around center
        offsetY={shape.height / 2}
        rotation={shape.rotation || 0}
        scaleX={shape.scaleX || 1}
        scaleY={shape.scaleY || 1}
        opacity={opacity}
        draggable={true}
        onClick={() => handleShapeClick(shape.id)}
        onTap={() => handleShapeClick(shape.id)}
        onTransform={() => handleShapeTransform(shape.id)}
        onDragEnd={() => handleShapeDragEnd(shape.id)}
      />
    </React.Fragment>
  );
};

// Legacy SVG Arrow Component (kept for backward compatibility)
const SvgArrow = ({ shape, opacity, handleShapeClick, handleShapeTransform, handleShapeDragEnd, shapeRefs }) => {
  const [image] = useImage(arrowSvg);
  
  return (
    <Image
      key={shape.id}
      ref={node => {
        if (node) {
          shapeRefs.current[shape.id] = node;
        }
      }}
      image={image}
      x={shape.x}
      y={shape.y}
      width={shape.width}
      height={shape.height}
      offsetX={shape.width / 2}  // Rotate around center X
      offsetY={shape.height / 2} // Rotate around center Y
      opacity={opacity}
      rotation={shape.rotation || 0}
      scaleX={shape.scaleX || 1}
      scaleY={shape.scaleY || 1}
      draggable={true}
      onClick={() => handleShapeClick(shape.id)}
      onTap={() => handleShapeClick(shape.id)}
      onTransform={() => handleShapeTransform(shape.id)}
      onDragEnd={() => handleShapeDragEnd(shape.id)}
    />
  );
};

// Timeline constants
const TIMELINE_HEIGHT = 240;
const TRACK_HEIGHT = 60;
const PIXELS_PER_SECOND = 50;
const PLAYHEAD_COLOR = '#ff0000';
const TRACK_LABEL_WIDTH = 80; // Width reserved for track labels


// CanvasOverlay component for text and shape handling
const CanvasOverlay = ({
  textLayers,
  setTextLayers,
  selectedTextId,
  setSelectedTextId,
  editingTextId,
  setEditingTextId,
  shapeLayers,
  setShapeLayers,
  selectedShapeId,
  setSelectedShapeId,
  currentTime,
  canvasScale
}) => {
  const textRefs = useRef({});
  const shapeRefs = useRef({});
  const transformerRef = useRef();
  const draggedAnchor = useRef(null);
  // Removed complex blur video capture - using CSS backdrop filter instead
  

  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && !editingTextId) {
      if (selectedTextId) {
        const selectedNode = textRefs.current[selectedTextId];
        if (selectedNode) {
          transformerRef.current.nodes([selectedNode]);
        }
      } else if (selectedShapeId) {
        const selectedNode = shapeRefs.current[selectedShapeId];
        if (selectedNode) {
          transformerRef.current.nodes([selectedNode]);
        }
      } else {
        transformerRef.current.nodes([]);
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
  }, [selectedTextId, selectedShapeId, editingTextId, textLayers, shapeLayers]);

  const handleTextDblClick = useCallback((textId) => {
    setEditingTextId(textId);
    setSelectedTextId(textId);
  }, [setEditingTextId, setSelectedTextId]);


  const handleTextTransform = useCallback((textId) => {
    const node = textRefs.current[textId];
    if (!node) return;
    
    const scaleX = node.scaleX();
    const newWidth = Math.max(30, node.width() * scaleX);
    
    setTextLayers(layers => layers.map(layer =>
      layer.id === textId
        ? {
            ...layer,
            width: newWidth,
            scaleX: 1,
            x: node.x(),
            y: node.y(),
            rotation: node.rotation()
          }
        : layer
    ));
  }, [setTextLayers]);

  const handleTextDragEnd = useCallback((textId) => {
    const node = textRefs.current[textId];
    if (!node) return;
    
    setTextLayers(layers => layers.map(layer =>
      layer.id === textId
        ? {
            ...layer,
            x: node.x(), // For left alignment, x is the left edge
            y: node.y()
          }
        : layer
    ));
  }, [setTextLayers]);

  // Shape handlers
  const handleShapeClick = useCallback((shapeId) => {
    setSelectedShapeId(shapeId);
    setSelectedTextId(null);
  }, [setSelectedShapeId, setSelectedTextId]);


  const handleShapeTransform = useCallback((shapeId) => {
    const node = shapeRefs.current[shapeId];
    if (!node) return;
    
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    setShapeLayers(layers => layers.map(layer => {
      if (layer.id !== shapeId) return layer;
      
      const shapeType = layer.shapeType || 'rectangle';
      
      if (shapeType === 'circle') {
        // For circles, we need to adjust position since they use center coordinates
        const newRadius = Math.max(15, (layer.width * Math.min(scaleX, scaleY)) / 2);
        const newWidth = newRadius * 2;
        const newHeight = newRadius * 2;
        
        return {
          ...layer,
          width: newWidth,
          height: newHeight,
          scaleX: 1,
          scaleY: 1,
          x: node.x() - newRadius,  // Convert center to top-left
          y: node.y() - newRadius,
          rotation: node.rotation()
        };
      } else if (shapeType === 'arrow') {
        // Arrow with container coordinates
        const newWidth = Math.max(30, node.width() * scaleX);
        const newHeight = Math.max(30, node.height() * scaleY);
        const rotation = node.rotation();
        const arrowX = node.x();
        const arrowY = node.y();
        
        // Calculate arrow container (bounding box) coordinates
        const angleRad = rotation * Math.PI / 180;
        const cos = Math.abs(Math.cos(angleRad));
        const sin = Math.abs(Math.sin(angleRad));
        const boundingWidth = newWidth * cos + newHeight * sin;
        const boundingHeight = newWidth * sin + newHeight * cos;
        
        return {
          ...layer,
          width: newWidth,
          height: newHeight,
          scaleX: 1,
          scaleY: 1,
          x: arrowX,
          y: arrowY,
          rotation: rotation,
          // Arrow container coordinates (bounding box)
          containerX: arrowX - boundingWidth/2,
          containerY: arrowY - boundingHeight/2,
          containerWidth: boundingWidth,
          containerHeight: boundingHeight
        };
      } else {
        // Rectangle - use Konva's native scaling instead of manual width/height calculation
        return {
          ...layer,
          x: node.x(),
          y: node.y(),
          rotation: node.rotation(),
          scaleX: scaleX, // No minimum scale restriction
          scaleY: scaleY  // Keep original width/height and let Konva handle scaling
        };
      }
    }));
  }, [setShapeLayers]);

  const handleShapeDragEnd = useCallback((shapeId) => {
    const node = shapeRefs.current[shapeId];
    if (!node) return;
    
    setShapeLayers(layers => layers.map(layer => {
      if (layer.id !== shapeId) return layer;
      
      const shapeType = layer.shapeType || 'rectangle';
      
      if (shapeType === 'circle') {
        // For circles, convert center position back to top-left
        const radius = Math.max(0, Math.min(Math.abs(layer.width), Math.abs(layer.height)) / 2);
        return {
          ...layer,
          x: node.x() - radius,
          y: node.y() - radius
        };
      } else if (shapeType === 'arrow') {
        // Arrow with container coordinates update
        const arrowX = node.x();
        const arrowY = node.y();
        const rotation = layer.rotation || 0;
        
        // Calculate arrow container (bounding box) coordinates
        const angleRad = rotation * Math.PI / 180;
        const cos = Math.abs(Math.cos(angleRad));
        const sin = Math.abs(Math.sin(angleRad));
        const boundingWidth = layer.width * cos + layer.height * sin;
        const boundingHeight = layer.width * sin + layer.height * cos;
        
        return {
          ...layer,
          x: arrowX,
          y: arrowY,
          // Arrow container coordinates (bounding box)
          containerX: arrowX - boundingWidth/2,
          containerY: arrowY - boundingHeight/2,
          containerWidth: boundingWidth,
          containerHeight: boundingHeight
        };
      } else {
        // Rectangle use standard top-left positioning
        return {
          ...layer,
          x: node.x(),
          y: node.y()
        };
      }
    }));
  }, [setShapeLayers]);

  return (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: `${1920 * canvasScale}px`,
      height: `${1080 * canvasScale}px`,
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      zIndex: 25
    }}>
      {/* HTML-based blur overlay layer - positioned before Stage */}
      {shapeLayers.some(shape => 
        currentTime >= shape.startTime && 
        currentTime <= shape.endTime && 
        shape.mode === 'blur' &&
        (shape.shapeType === 'rectangle' || shape.shapeType === 'circle')
      ) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1
          }}
        >
          {shapeLayers.map(shape => {
            const isVisible = currentTime >= shape.startTime && currentTime <= shape.endTime;
            if (!isVisible || shape.mode !== 'blur') return null;
            
            const shapeType = shape.shapeType || 'rectangle';
            
            // Only support rectangle and circle for blur mode
            if (shapeType !== 'rectangle' && shapeType !== 'circle') return null;
            
            // Blur layers don't fade - they're either visible or not
            const opacity = shape.opacity || 1;
            
            // Scale coordinates to match canvas display
            const scaleX = canvasScale;
            const scaleY = canvasScale;
            const scaledX = shape.x * scaleX;
            const scaledY = shape.y * scaleY;
            const effectiveWidth = shape.width * (shape.scaleX || 1);
            const effectiveHeight = shape.height * (shape.scaleY || 1);
            const scaledWidth = effectiveWidth * scaleX;
            const scaledHeight = effectiveHeight * scaleY;
            
            const commonStyles = {
              position: 'absolute',
              left: `${scaledX}px`,
              top: `${scaledY}px`,
              width: `${scaledWidth}px`,
              height: `${scaledHeight}px`,
              opacity: opacity,
              transform: `rotate(${shape.rotation || 0}deg)`,
              transformOrigin: 'center center',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)', // Safari support
              border: '2px solid rgba(255, 255, 255, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              pointerEvents: 'none'
            };
            
            if (shapeType === 'circle') {
              return (
                <div
                  key={`html-blur-${shape.id}`}
                  style={{
                    ...commonStyles,
                    borderRadius: '50%'
                  }}
                />
              );
            } else {
              // Rectangle
              return (
                <div
                  key={`html-blur-${shape.id}`}
                  style={commonStyles}
                />
              );
            }
          })}
        </div>
      )}

      <Stage
        width={1920}
        height={1080}
        scaleX={canvasScale}
        scaleY={canvasScale}
        style={{ 
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 2
        }}
      >
        <Layer>
          {textLayers.map(textLayer => {
            // Check if text is visible at current time
            const isVisible = currentTime >= textLayer.startTime && currentTime <= textLayer.endTime;
            if (!isVisible) return null;
            
            const isSelected = selectedTextId === textLayer.id;
            const isEditing = editingTextId === textLayer.id;
            
            // Calculate opacity based on fade timing
            let opacity = textLayer.opacity || 1;
            const timeSinceStart = currentTime - textLayer.startTime;
            const timeUntilEnd = textLayer.endTime - currentTime;
            
            if (textLayer.fadeInDuration && timeSinceStart < textLayer.fadeInDuration) {
              opacity = (timeSinceStart / textLayer.fadeInDuration) * textLayer.opacity;
            } else if (textLayer.fadeOutDuration && timeUntilEnd < textLayer.fadeOutDuration) {
              opacity = (timeUntilEnd / textLayer.fadeOutDuration) * textLayer.opacity;
            }
            
            return (
              <React.Fragment key={textLayer.id}>
                <Text
                  ref={node => {
                    if (node) {
                      textRefs.current[textLayer.id] = node;
                    }
                  }}
                  text={textLayer.text}
                  x={textLayer.x}
                  y={textLayer.y}
                  fontSize={textLayer.fontSize}
                  fontFamily={textLayer.fontFamily}
                  fontStyle={textLayer.fontStyle}
                  fill={textLayer.fill}
                  align={textLayer.align}
                  width={textLayer.width}
                  rotation={textLayer.rotation}
                  scaleX={textLayer.scaleX}
                  scaleY={textLayer.scaleY}
                  opacity={opacity}
                  padding={textLayer.padding}
                  wrap="none"
                  draggable={!isEditing}
                  visible={!isEditing}
                  listening={true}
                  onDblClick={() => handleTextDblClick(textLayer.id)}
                  onDblTap={() => handleTextDblClick(textLayer.id)}
                  onClick={() => {
                    setSelectedTextId(textLayer.id);
                    setSelectedShapeId(null);
                  }}
                  onTap={() => {
                    setSelectedTextId(textLayer.id);
                    setSelectedShapeId(null);
                  }}
                  onTransform={() => handleTextTransform(textLayer.id)}
                  onDragEnd={() => handleTextDragEnd(textLayer.id)}
                />
                
              </React.Fragment>
            );
          })}
          

          {/* Render shapes */}
          {shapeLayers.map(shape => {
            // Check if shape is visible at current time
            const isVisible = currentTime >= shape.startTime && currentTime <= shape.endTime;
            if (!isVisible) return null;
            
            
            // Note: Blur shapes are rendered normally for selection, with HTML overlay providing the blur effect
            
            // Fallback: if arrow somehow has blur mode, reset it to outline
            const shapeType = shape.shapeType || 'rectangle';
            if (shapeType === 'arrow' && shape.mode === 'blur') {
              // Reset arrow to outline mode if it somehow got blur mode
              shape.mode = 'outline';
            }
            
            const isSelected = selectedShapeId === shape.id;
            
            // Calculate opacity based on fade timing
            let opacity = shape.opacity || 1;
            const timeSinceStart = currentTime - shape.startTime;
            const timeUntilEnd = shape.endTime - currentTime;
            
            if (shape.fadeInDuration && timeSinceStart < shape.fadeInDuration) {
              opacity = (timeSinceStart / shape.fadeInDuration) * shape.opacity;
            } else if (shape.fadeOutDuration && timeUntilEnd < shape.fadeOutDuration) {
              opacity = (timeUntilEnd / shape.fadeOutDuration) * shape.opacity;
            }
            
            // Determine shape appearance based on mode
            const currentMode = shape.mode || 'outline';
            let fill = 'transparent';
            let stroke = 'transparent';
            let strokeWidth = 0;
            
            if (currentMode === 'outline') {
              stroke = shape.stroke || '#ff0000';
              strokeWidth = shape.strokeWidth || 3;
              fill = 'transparent'; // No fill for outline mode
            } else if (currentMode === 'fill') {
              fill = shape.fill || '#ff0000';
            } else if (currentMode === 'blur') {
              // For blur mode, render invisible shape for selection (HTML overlay provides the blur effect)
              fill = 'rgba(255, 255, 255, 0.01)'; // Nearly transparent fill for clicking
              stroke = 'transparent'; // No stroke - transformation controllers handle selection feedback
              strokeWidth = 0;
            }
            
            // Render different shape types (shapeType already declared above)
            if (shapeType === 'circle') {
              return (
                <Circle
                  key={shape.id}
                  ref={node => {
                    if (node) {
                      shapeRefs.current[shape.id] = node;
                    }
                  }}
                  x={shape.x + shape.width / 2}  // Circle center X
                  y={shape.y + shape.height / 2} // Circle center Y
                  radius={Math.max(0, Math.min(Math.abs(shape.width), Math.abs(shape.height)) / 2)}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  strokeScaleEnabled={false}
                  opacity={opacity}
                  rotation={shape.rotation || 0}
                  scaleX={shape.scaleX || 1}
                  scaleY={shape.scaleY || 1}
                  draggable={true}
                  onClick={() => handleShapeClick(shape.id)}
                  onTap={() => handleShapeClick(shape.id)}
                  onTransform={() => handleShapeTransform(shape.id)}
                  onDragEnd={() => handleShapeDragEnd(shape.id)}
                />
              );
            } else if (shapeType === 'arrow') {
              return (
                <ArrowContainer
                  shape={shape}
                  opacity={opacity}
                  handleShapeClick={handleShapeClick}
                  handleShapeTransform={handleShapeTransform}
                  handleShapeDragEnd={handleShapeDragEnd}
                  shapeRefs={shapeRefs}
                />
              );
            } else {
              // Default rectangle
              return (
                <Rect
                  key={shape.id}
                  ref={node => {
                    if (node) {
                      shapeRefs.current[shape.id] = node;
                    }
                  }}
                  x={shape.x}
                  y={shape.y}
                  width={shape.width}
                  height={shape.height}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  strokeScaleEnabled={false}
                  opacity={opacity}
                  rotation={shape.rotation || 0}
                  scaleX={shape.scaleX || 1}
                  scaleY={shape.scaleY || 1}
                  draggable={true}
                  onClick={() => handleShapeClick(shape.id)}
                  onTap={() => handleShapeClick(shape.id)}
                  onTransform={() => handleShapeTransform(shape.id)}
                  onDragEnd={() => handleShapeDragEnd(shape.id)}
                />
              );
            }
          })}
          
          {!editingTextId && (selectedTextId || selectedShapeId) && (
            <Transformer
              ref={transformerRef}
              listening={true}
              enabledAnchors={selectedTextId ? ['middle-left', 'middle-right'] : ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right', 'top-center', 'bottom-center']}
              boundBoxFunc={(oldBox, newBox) => {
                if (selectedTextId) {
                  // Text: only allow width changes, keep height constant
                  return {
                    ...newBox,
                    width: Math.max(50, newBox.width), // Simple minimum width, no snapping
                    height: oldBox.height
                  };
                } else {
                  // Shapes: allow unrestricted resize
                  return newBox;
                }
              }}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
};

// Waveform generation hook
const useWaveform = (audioUrl) => {
  const [waveformData, setWaveformData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!audioUrl) {
      return;
    }

    const generateWaveform = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch audio file
        const response = await fetch(audioUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.status}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        
        // Create audio context
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        // Get audio data (use first channel if stereo)
        const channelData = audioBuffer.getChannelData(0);
        const sampleRate = audioBuffer.sampleRate;
        const duration = audioBuffer.duration;
        
        // Generate waveform peaks (sample every N samples for performance)
        const samplesPerPixel = Math.floor(channelData.length / 1000); // ~1000 data points
        const peaks = [];
        
        for (let i = 0; i < channelData.length; i += samplesPerPixel) {
          let min = 0;
          let max = 0;
          
          // Find min/max in this sample window
          for (let j = 0; j < samplesPerPixel && i + j < channelData.length; j++) {
            const sample = channelData[i + j];
            if (sample > max) max = sample;
            if (sample < min) min = sample;
          }
          
          peaks.push({ min, max });
        }
        
        setWaveformData({
          peaks,
          duration,
          sampleRate,
          length: channelData.length
        });
        
        // Clean up audio context
        await audioContext.close();
        
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    generateWaveform();
  }, [audioUrl]);

  return { waveformData, isLoading, error };
};

// Waveform visualization component
const Waveform = ({ waveformData, clipWidth, clipHeight, audioOffset = 0, clipDuration, audioUrl }) => {
  if (!waveformData || !waveformData.peaks) {
    return null;
  }

  const { peaks, duration: audioDuration } = waveformData;
  
  // Calculate which part of the waveform to show based on audio offset and clip duration
  const startRatio = audioOffset / audioDuration;
  const endRatio = (audioOffset + clipDuration) / audioDuration;
  
  const startIndex = Math.floor(startRatio * peaks.length);
  const endIndex = Math.min(Math.ceil(endRatio * peaks.length), peaks.length);
  const visiblePeaks = peaks.slice(startIndex, endIndex);
  
  // Create waveform path
  const sceneFunc = (context, shape) => {
    context.beginPath();
    
    // Calculate bar width based on the original full waveform scale, not the visible portion
    const originalBarWidth = clipWidth / (peaks.length * (clipDuration / audioDuration));
    const centerY = clipHeight / 2;
    const scale = clipHeight * 0.8; // Use 80% of clip height for waveform
    
    visiblePeaks.forEach((peak, index) => {
      const x = index * originalBarWidth;
      const maxHeight = Math.abs(peak.max) * scale / 2;
      const minHeight = Math.abs(peak.min) * scale / 2;
      
      // Draw waveform bar (from center outward)
      context.moveTo(x, centerY - maxHeight);
      context.lineTo(x, centerY + minHeight);
    });
    
    context.strokeStyle = '#7ed321';
    context.lineWidth = Math.max(1, originalBarWidth * 0.8);
    context.globalAlpha = 0.8;
    context.stroke();
    
    context.fillStrokeShape(shape);
  };

  return (
    <Shape
      sceneFunc={sceneFunc}
      listening={false} // Don't interfere with clip interactions
    />
  );
};

// Timeline Track Component
const TimelineTrack = ({
  trackType,
  clips,
  duration,
  availableWidth,
  onClipDrag,
  onClipResize,
  onClipSplit,
  selectedClipId,
  onClipSelect,
  onClipRightClick = null,
  waveformData = null,
  trackIndex = 0,
  videoTrackOffset = 0,
  audioTrackOffset = 0,
  onRippleDelete
}) => {
  // Calculate trackY based on type and offsets (no need for ruler offset since it's in scrollable area)
  let trackY;
  if (trackType === 'text') {
    trackY = trackIndex * 70; // Each text track gets 70px spacing, starting from 0
  } else if (trackType === 'shapes') {
    trackY = trackIndex * 70; // Each shape track gets 70px spacing, positioned after text tracks
  } else if (trackType === 'video') {
    trackY = videoTrackOffset * 70; // Offset by number of shape tracks
  } else { // audio
    trackY = (audioTrackOffset * 70) + 70; // Offset by shape tracks + video track
  }
  const contentWidth = availableWidth - TRACK_LABEL_WIDTH;
  const pixelsPerSecond = duration > 0 ? contentWidth / duration : PIXELS_PER_SECOND;

  // Helper function to check if a time position is in an empty space
  const isEmptySpace = (timePosition) => {
    // Check if the time position overlaps with any existing clip
    return !clips.some(clip => 
      timePosition >= clip.startTime && timePosition <= clip.endTime
    );
  };

  // Helper function to find clips that need to be moved during ripple delete
  const findClipsToRipple = (gapStartTime, gapEndTime) => {
    return clips.filter(clip => clip.startTime >= gapEndTime);
  };

  // Helper function to calculate gap size at a given position
  const calculateGapSize = (timePosition) => {
    // Sort clips by start time
    const sortedClips = [...clips].sort((a, b) => a.startTime - b.startTime);
    
    // Find the gap where the click occurred
    let gapStart = 0;
    let gapEnd = duration;
    
    for (let i = 0; i < sortedClips.length; i++) {
      const clip = sortedClips[i];
      
      if (timePosition < clip.startTime) {
        // Click is before this clip
        gapEnd = clip.startTime;
        break;
      } else if (timePosition >= clip.startTime && timePosition <= clip.endTime) {
        // Click is inside a clip - not a gap
        return null;
      } else {
        // Click is after this clip
        gapStart = clip.endTime;
      }
    }
    
    return { gapStart, gapEnd };
  };

  // Right-click handler for the track content area
  const handleTrackRightClick = (e) => {
    e.evt.preventDefault();
    
    // Get click position in timeline
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    const clickX = pointer.x - TRACK_LABEL_WIDTH;
    const timePosition = clickX / pixelsPerSecond;
    
    // Check if click is in an empty space
    if (!isEmptySpace(timePosition)) {
      return; // Don't show context menu if clicking on a clip
    }
    
    // Calculate gap information
    const gapInfo = calculateGapSize(timePosition);
    if (!gapInfo) return;
    
    const { gapStart, gapEnd } = gapInfo;
    const gapDuration = gapEnd - gapStart;
    
    // Only show ripple delete if there's actually a gap and clips to move
    const clipsToMove = findClipsToRipple(gapStart, gapEnd);
    if (gapDuration <= 0 || clipsToMove.length === 0) return;
    
    // Get the stage container's position to calculate screen coordinates
    const stageContainer = stage.container();
    const containerRect = stageContainer.getBoundingClientRect();
    
    // Calculate screen position for the context menu
    // Position it at the track level, not at the cursor position
    const screenX = containerRect.left + pointer.x;
    const screenY = containerRect.top + trackY + (TRACK_HEIGHT / 2); // Center vertically in the track
    
    // Show context menu
    if (onRippleDelete) {
      onRippleDelete(trackType, gapStart, gapEnd, clipsToMove, { x: screenX, y: screenY });
    }
  };
  
  return (
    <Group>
      {/* Track label area */}
      <Rect
        x={0}
        y={trackY}
        width={TRACK_LABEL_WIDTH}
        height={TRACK_HEIGHT}
        fill="#333"
        stroke="#444"
        strokeWidth={1}
      />
      
      {/* Track content area */}
      <Rect
        x={TRACK_LABEL_WIDTH}
        y={trackY}
        width={availableWidth - TRACK_LABEL_WIDTH}
        height={TRACK_HEIGHT}
        fill={trackType === 'video' ? '#2a2a2a' : '#1a1a1a'}
        stroke="#444"
        strokeWidth={1}
        onContextMenu={handleTrackRightClick}
      />
      
      {/* Track label */}
      <Text
        x={10}
        y={trackY + 20}
        text={trackType.toUpperCase()}
        fontSize={12}
        fill="#fff"
        fontFamily="Arial"
      />
      
      {/* Clips */}
      {clips.map((clip, index) => (
        <ClipBlock
          key={clip.id}
          clip={clip}
          trackY={trackY}
          pixelsPerSecond={pixelsPerSecond}
          isSelected={clip.id === selectedClipId}
          onDrag={onClipDrag}
          onResize={onClipResize}
          onSplit={onClipSplit}
          onSelect={onClipSelect}
          onRightClick={onClipRightClick}
          waveformData={trackType === 'audio' ? waveformData : null}
        />
      ))}
    </Group>
  );
};

// Individual Clip Block Component
const ClipBlock = ({
  clip,
  trackY,
  pixelsPerSecond,
  isSelected,
  onDrag,
  onResize,
  onSplit,
  onSelect,
  onRightClick = null,
  waveformData = null
}) => {
  const clipWidth = Math.max(0, (clip.endTime - clip.startTime) * pixelsPerSecond);
  const clipX = TRACK_LABEL_WIDTH + (clip.startTime * pixelsPerSecond);
  
  return (
    <Group>
      {/* Main clip rectangle */}
      <Rect
        x={clipX}
        y={trackY + 2}
        width={clipWidth}
        height={Math.max(0, TRACK_HEIGHT - 4)}
        fill={clip.type === 'video' ? '#4a90e2' : clip.type === 'frame' ? '#9b59b6' : clip.type === 'shape' ? '#b85450' : clip.type === 'text' ? '#e67e22' : '#1a3a1a'}
        stroke={isSelected ? 'transparent' : '#333'}
        strokeWidth={isSelected ? 0 : 1}
        cornerRadius={Math.min(4, Math.min(clipWidth, TRACK_HEIGHT - 4) / 2)}
        draggable
        onClick={() => onSelect(clip.id)}
        onContextMenu={(e) => {
          // Only show context menu for shape and text layers
          if ((clip.type === 'shape' || clip.type === 'text') && onRightClick) {
            // Konva event handling - access the native event
            const evt = e.evt;
            if (evt && evt.preventDefault) {
              evt.preventDefault();
            }
            onRightClick(evt || e, clip.type, clip.id, clip);
          }
        }}
        dragBoundFunc={(pos) => ({
          x: Math.max(TRACK_LABEL_WIDTH, pos.x),
          y: trackY + 2 // Constrain to horizontal movement only
        })}
        onDragStart={() => {
          // Set dragging state to prevent deselection
          if (window.setTimelineDragState) {
            window.setTimelineDragState(true);
          }
        }}
        onDragEnd={() => {
          // Clear dragging state
          if (window.setTimelineDragState) {
            window.setTimelineDragState(false);
          }
        }}
        onDragMove={(e) => {
          const newStartTime = Math.max(0, (e.target.x() - TRACK_LABEL_WIDTH) / pixelsPerSecond);
          onDrag(clip.id, newStartTime);
        }}
      />
      
      {/* Waveform for audio clips */}
      {clip.type === 'audio' && waveformData && (
        <Group
          x={clipX}
          y={trackY + 5}
          clipX={0}
          clipY={0}
          clipWidth={clipWidth}
          clipHeight={TRACK_HEIGHT - 10}
        >
          <Waveform
            waveformData={waveformData}
            clipWidth={clipWidth}
            clipHeight={TRACK_HEIGHT - 10}
            audioOffset={clip.audioOffset || 0}
            clipDuration={clip.endTime - clip.startTime}
          />
        </Group>
      )}
      
      {/* Clip name */}
      <Text
        x={clipX + 5}
        y={trackY + 15}
        text={(() => {
          const fullText = clip.type === 'frame' ? clip.name : `${clip.type} ${clip.id}`;
          const maxWidth = clipWidth - 10;
          const avgCharWidth = 6; // Approximate character width for Arial 10px
          const maxChars = Math.floor(maxWidth / avgCharWidth);

          if (maxChars <= 3) return '...';
          if (fullText.length <= maxChars) return fullText;

          return fullText.substring(0, maxChars - 3) + '...';
        })()}
        fontSize={10}
        fill={clip.type === 'audio' ? '#7ed321' : '#fff'}
        fontFamily="Arial"
        width={clipWidth - 10}
        onClick={() => onSelect(clip.id)}
        onTap={() => onSelect(clip.id)}
      />

      {/* Selection outline inside clip */}
      {isSelected && (
        <Rect
          x={clipX + 1}
          y={trackY + 3}
          width={Math.max(0, clipWidth - 2)}
          height={Math.max(0, TRACK_HEIGHT - 6)}
          fill="transparent"
          stroke="#fff"
          strokeWidth={2}
          cornerRadius={Math.min(4, Math.min(Math.max(0, clipWidth - 2), TRACK_HEIGHT - 6) / 2)}
          listening={false}
        />
      )}

      {/* Resize handles */}
      {isSelected && (
        <>
          {/* Left resize handle */}
          <Rect
            x={clipX + 2}
            y={trackY + 4}
            width={6}
            height={TRACK_HEIGHT - 8}
            fill="#fff"
            opacity={0.8}
            draggable
            onClick={(e) => {
              e.cancelBubble = true; // Prevent main clip click
              e.evt.stopPropagation(); // Prevent stage click handler
            }}
            onMouseDown={(e) => {
              e.evt.stopPropagation(); // Prevent stage mouse events
            }}
            dragBoundFunc={(pos) => ({
              x: Math.max(TRACK_LABEL_WIDTH + 2, Math.min(pos.x, clipX + clipWidth - 10)),
              y: trackY + 4
            })}
            onDragStart={() => {
              if (window.setTimelineDragState) {
                window.setTimelineDragState(true);
              }
            }}
            onDragEnd={() => {
              if (window.setTimelineDragState) {
                window.setTimelineDragState(false);
              }
            }}
            onDragMove={(e) => {
              const newStartTime = (e.target.x() - TRACK_LABEL_WIDTH - 2) / pixelsPerSecond;
              onResize(clip.id, 'start', newStartTime);
            }}
          />
          
          {/* Right resize handle */}
          <Rect
            x={clipX + clipWidth - 8}
            y={trackY + 4}
            width={6}
            height={TRACK_HEIGHT - 8}
            fill="#fff"
            opacity={0.8}
            draggable
            onClick={(e) => {
              e.cancelBubble = true; // Prevent main clip click
              e.evt.stopPropagation(); // Prevent stage click handler
            }}
            onMouseDown={(e) => {
              e.evt.stopPropagation(); // Prevent stage mouse events
            }}
            dragBoundFunc={(pos) => ({
              x: Math.max(clipX + 10, pos.x),
              y: trackY + 4
            })}
            onDragStart={() => {
              if (window.setTimelineDragState) {
                window.setTimelineDragState(true);
              }
            }}
            onDragEnd={() => {
              if (window.setTimelineDragState) {
                window.setTimelineDragState(false);
              }
            }}
            onDragMove={(e) => {
              const newEndTime = (e.target.x() + 6 - TRACK_LABEL_WIDTH) / pixelsPerSecond;
              onResize(clip.id, 'end', newEndTime);
            }}
          />
        </>
      )}
    </Group>
  );
};

// Playhead Component
const Playhead = ({ currentTime, duration, availableWidth, height = TIMELINE_HEIGHT, lineOnly = false }) => {
  const contentWidth = availableWidth - TRACK_LABEL_WIDTH;
  const pixelsPerSecond = duration > 0 ? contentWidth / duration : PIXELS_PER_SECOND;
  const x = TRACK_LABEL_WIDTH + (currentTime * pixelsPerSecond);
  
  return (
    <Group>
      <Line
        points={[x, 0, x, height]}
        stroke={PLAYHEAD_COLOR}
        strokeWidth={2}
      />
      {!lineOnly && (
        <Rect
          x={x - 8}
          y={0}
          width={16}
          height={20}
          fill={PLAYHEAD_COLOR}
          cornerRadius={3}
        />
      )}
    </Group>
  );
};

// Time Ruler Component
const TimeRuler = ({ duration, availableWidth, onSeek, interactive = false }) => {
  const rulers = [];
  const contentWidth = availableWidth - TRACK_LABEL_WIDTH;
  const pixelsPerSecond = duration > 0 ? contentWidth / duration : PIXELS_PER_SECOND;
  
  // Create ruler marks at reasonable intervals
  const secondInterval = duration > 20 ? 5 : duration > 10 ? 2 : 1;
  const seconds = Math.ceil(duration);
  
  for (let i = 0; i <= seconds; i += secondInterval) {
    const x = TRACK_LABEL_WIDTH + (i * pixelsPerSecond);
    if (x <= availableWidth) {
      rulers.push(
        <Group key={i}>
          <Line
            points={[x, 0, x, 20]}
            stroke="#666"
            strokeWidth={1}
          />
          <Text
            x={x + 2}
            y={5}
            text={`${i}s`}
            fontSize={10}
            fill="#ccc"
            fontFamily="Arial"
          />
        </Group>
      );
    }
  }

  // Add interactive scrubbing area if needed
  const scrubArea = interactive && onSeek ? (
    <Rect
      x={TRACK_LABEL_WIDTH}
      y={0}
      width={contentWidth}
      height={40}
      fill="transparent"
      onClick={(e) => {
        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();
        const timelineTime = Math.max(-0.1, Math.min((pos.x - TRACK_LABEL_WIDTH) / pixelsPerSecond, duration));
        onSeek(timelineTime);
      }}
      onMouseDown={(e) => {
        // Initiate scrubbing on mouse down
        const stage = e.target.getStage();
        const pos = stage.getPointerPosition();
        const timelineTime = Math.max(-0.1, Math.min((pos.x - TRACK_LABEL_WIDTH) / pixelsPerSecond, duration));
        onSeek(timelineTime);
      }}
      onMouseMove={(e) => {
        if (e.evt.buttons === 1) { // Left mouse button is pressed (dragging)
          // Throttle mouse move updates to prevent excessive seeking
          if (!window._lastScrubTime || Date.now() - window._lastScrubTime > 16) { // ~60fps limit
            const stage = e.target.getStage();
            const pos = stage.getPointerPosition();
            const timelineTime = Math.max(-0.1, Math.min((pos.x - TRACK_LABEL_WIDTH) / pixelsPerSecond, duration));
            onSeek(timelineTime);
            window._lastScrubTime = Date.now();
          }
        }
      }}
      onMouseEnter={() => {
        const container = document.querySelector('.timeline-fixed-layer');
        if (container) container.style.cursor = 'pointer';
      }}
      onMouseLeave={() => {
        const container = document.querySelector('.timeline-fixed-layer');
        if (container) container.style.cursor = 'default';
      }}
    />
  ) : null;
  
  return (
    <Group>
      {rulers}
      {scrubArea}
    </Group>
  );
};

function ClipEditorPage() {
  const { projectId, itemId } = useParams();
  const navigate = useNavigate();
  
  // Language context - get from localStorage (same as VideoEditorPage)
  const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('selectedLanguage') || 'original');
  
  // Log language changes for debugging
  useEffect(() => {
  }, [currentLanguage]);
  const [hasCustomConfig, setHasCustomConfig] = useState(false);
  const [configurationLoaded, setConfigurationLoaded] = useState(false);
  
  // State management
  const [timelineItem, setTimelineItem] = useState(null);
  const [project, setProject] = useState(null);
  const [timelineItems, setTimelineItems] = useState([]);
  const [itemIndex, setItemIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Video generation modal state
  const [isVideoGenerationModalOpen, setIsVideoGenerationModalOpen] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoGenerationProgress, setVideoGenerationProgress] = useState(null);
  const [videoGenerationResult, setVideoGenerationResult] = useState(null);
  const [videoGenerationError, setVideoGenerationError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, originalSetCurrentTime] = useState(0);

  // CRITICAL FIX: Block webpack hot reload interference
  const setCurrentTime = useCallback((value) => {
    const oldTime = currentTime;
    const newTime = typeof value === 'function' ? value(oldTime) : value;
    const stack = new Error().stack?.split('\n')[2]?.trim() || 'unknown';

    // CRITICAL: Block updates from webpack hot reload during development
    if (stack.includes('hot-update.js') || stack.includes('webpack')) {
      return; // Block the timeline update
    }

    // DEBUG: Log important setCurrentTime calls (reduced verbosity)
    if (Math.abs(newTime - oldTime) > 0.5 || newTime < oldTime) {
      const caller = stack.split('(')[0]?.replace('at ', '') || 'unknown';
    }

    // Log backwards jumps (real issues)
    if (newTime < oldTime - 1.0 || (oldTime > 8.0 && newTime < 2.0)) {
    }

    originalSetCurrentTime(value);
  }, [originalSetCurrentTime]); // CRITICAL: Remove currentTime from deps to prevent recreation
  const [duration, setDuration] = useState(0);
  const [isLooping, setIsLooping] = useState(false);
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [actualAudioDuration, setActualAudioDuration] = useState(null);
  const [actualVideoDuration, setActualVideoDuration] = useState(null);
  const [timelineKey, setTimelineKey] = useState(0); // Force re-render key
  const [audioUrlKey, setAudioUrlKey] = useState(0); // Force audioUrl recalculation
  const [showResetConfirm, setShowResetConfirm] = useState(false); // Reset confirmation modal
  const [customDuration, setCustomDuration] = useState(0); // Custom duration input
  const [selectedShapeId, setSelectedShapeId] = useState(null); // Selected shape for transformation
  const [shapeMode, setShapeMode] = useState('outline'); // Shape mode: outline, fill, blur
  const [showModeDropdown, setShowModeDropdown] = useState(false); // Mode selector dropdown
  const [showExtractFrameDropdown, setShowExtractFrameDropdown] = useState(false); // Extract frame dropdown

  // Text layer state
  const [textLayers, setTextLayers] = useState([]); // Text layers for overlay
  const [selectedTextId, setSelectedTextId] = useState(null); // Selected text for editing
  const [editingTextId, setEditingTextId] = useState(null); // Text currently being edited
  
  // Timeline data
  const [videoClips, setVideoClips] = useState([]);
  const [audioClips, setAudioClips] = useState([]);
  const [shapeLayers, setShapeLayers] = useState([]); // Shape layers for overlay
  const [canvasScale, setCanvasScale] = useState(1); // Scale factor for canvas display
  const [originalFitScale, setOriginalFitScale] = useState(null); // Store the original fit-to-screen scale
  
  // Enhanced logging wrapper for setDuration
  const setDurationWithLogging = useCallback((newDuration, source = 'unknown') => {
    const timestamp = new Date().toISOString();
    const stack = new Error().stack?.split('\n')[2]?.trim() || 'unknown';

    // 🚨 SPECIAL ALERT: Duration changes that might affect 8s-15s range
    if ((duration >= 8 && duration <= 15) || (newDuration >= 8 && newDuration <= 15) ||
        (duration < 15 && newDuration > 15) || (duration > 8 && newDuration < 8)) {
      // Duration change affects 8s-15s range - could cause problematic looping behavior
    }

    setDuration(newDuration);
  }, [duration, hasCustomConfig, configurationLoaded]);
  
  // Ripple Delete context menu state
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    trackType: '',
    gapStart: 0,
    gapEnd: 0,
    clipsToMove: [],
    gapDuration: 0
  });

  // Layer context menu state
  const [layerContextMenu, setLayerContextMenu] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    layerType: '', // 'shape' or 'text'
    layerId: null,
    layer: null
  });
  const [canvasReady, setCanvasReady] = useState(false);
  const [forceRedraw, setForceRedraw] = useState(0); // Canvas initialization state
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false); // Font size dropdown
  const [customFontSize, setCustomFontSize] = useState(''); // Custom font size input
  const [showShapeDropdown, setShowShapeDropdown] = useState(false); // Shape type dropdown
  const [showPasteModal, setShowPasteModal] = useState(false); // Paste shape modal
  const [pasteJson, setPasteJson] = useState(''); // JSON input for pasting shapes
  const [showScriptModal, setShowScriptModal] = useState(false); // Script editing modal
  const [scriptDescription, setScriptDescription] = useState(''); // Scene description input
  const [scriptNarration, setScriptNarration] = useState(''); // Scene narration input
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false); // Audio generation state
  const [isRegeneratingScript, setIsRegeneratingScript] = useState(false); // Script regeneration state
  const [showColorPicker, setShowColorPicker] = useState(false); // Color picker dropdown
  const [customColor, setCustomColor] = useState('#ff0000'); // Custom color picker value

  // Refs
  const playerRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const audioRef = useRef(null);
  const timelineStageRef = useRef(null);
  const configurationLoadedRef = useRef(false);
  // Removed blurVideoRefs - using CSS backdrop filter instead
  
  // Text width calculation utility
  const calculateTextWidth = useCallback((text, fontSize, fontFamily, fontStyle) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const weight = fontStyle === 'bold' ? 'bold' : 'normal';
    context.font = `${weight} ${fontSize || 48}px ${fontFamily || 'Arial'}`;
    return context.measureText(text || 'Text').width;
  }, []);

  // Handle text change with dynamic width calculation
  const handleTextChange = useCallback((textId, newText) => {
    setTextLayers(layers => layers.map(layer => {
      if (layer.id === textId) {
        // Calculate new width based on text content
        const textWidth = calculateTextWidth(newText, layer.fontSize, layer.fontFamily, layer.fontStyle);
        const newWidth = Math.max(textWidth + 10, 50); // 10px padding minimum
        return { 
          ...layer, 
          text: newText,
          width: newWidth
        };
      }
      return layer;
    }));
  }, [setTextLayers, calculateTextWidth]);

  // Handle font size change with dynamic width calculation
  const handleFontSizeChange = useCallback((textId, newFontSize) => {
    setTextLayers(layers => layers.map(layer => {
      if (layer.id === textId) {
        // Calculate new width based on new font size
        const textWidth = calculateTextWidth(layer.text, newFontSize, layer.fontFamily, layer.fontStyle);
        const newWidth = Math.max(textWidth + 10, 50); // 10px padding minimum
        return { 
          ...layer, 
          fontSize: newFontSize,
          width: newWidth
        };
      }
      return layer;
    }));
  }, [setTextLayers, calculateTextWidth]);
  
  // Generate waveform for audio if available - use language-specific path
  const audioUrl = useMemo(() => {
    
    let audioPath = null;
    
    if (currentLanguage === 'original') {
      // For original language, use scene_audio
      audioPath = timelineItem?.scene_audio;
    } else {
      // For alternate languages, look in translations first, then fallback to constructed path
      const translation = timelineItem?.translations?.find(t => t.language_code === currentLanguage);
      if (translation?.translated_audio_path) {
        audioPath = translation.translated_audio_path;
      } else {
        // Fallback to constructed path for backward compatibility
        audioPath = `/audio/timeline_item_audio_${itemId}_${currentLanguage}.mp3`;
      }
    }
    
    if (!audioPath) {
      return null;
    }
    
    const finalUrl = `${API_URL}${audioPath}`;
    
    // Add cache-busting when audioUrlKey changes (after regeneration)
    if (audioUrlKey > 0) {
      return `${finalUrl}?t=${Date.now()}`;
    }
    
    return finalUrl;
  }, [timelineItem?.scene_audio, timelineItem?.translations, currentLanguage, itemId, audioUrlKey]);
  const { waveformData, isLoading: waveformLoading, error: waveformError } = useWaveform(audioUrl);
  
  // Log waveform status
  useEffect(() => {
    if (waveformData) {
    }
    if (waveformError) {
    }
  }, [waveformData, waveformError]);

  // Initialize custom duration when duration changes
  useEffect(() => {
    if (duration > 0 && customDuration === 0) {
      setCustomDuration(duration);
    }
  }, [duration, customDuration]);
  
  // Track all duration changes for debugging
  useEffect(() => {
    // Duration state tracking (console logging removed)
  }, [duration, customDuration, hasCustomConfig, configurationLoaded, timelineItem]);
  
  // Initialize clip data and set proper timeline duration
  useEffect(() => {
    if (timelineItem && !configurationLoaded) { // Only run before configuration is loaded
      // Calculate initial video clip duration
      let videoClipDuration;
      if (timelineItem.type === 'intro' || timelineItem.type === 'outro') {
        // For intro/outro items, use a default duration since they don't have startTime/endTime
        videoClipDuration = 10.0; // Default 10 seconds, will be updated when video loads
      } else {
        videoClipDuration = (timelineItem.endTime || 0) - (timelineItem.startTime || 0);
      }
      
      // Construct language-specific audio path at the beginning of the effect  
      const languageSpecificAudioPath = currentLanguage === 'original' 
        ? timelineItem.scene_audio
        : `/audio/timeline_item_audio_${itemId}_${currentLanguage}.mp3`;
      
      
      // Set initial duration to video clip duration, will be updated when we get actual audio duration
      // Only set duration if configuration hasn't been loaded yet (ref-based check)
      if (!configurationLoadedRef.current) {
        setDurationWithLogging(videoClipDuration, 'timeline-initialization-video');
      } else {
      }
      setActualVideoDuration(videoClipDuration);
      
      // Initialize video clip - start at position 0 seconds
      const videoClip = {
        id: 'video-1',
        type: 'video',
        name: 'Video',
        startTime: 0.0, // Timeline position where this video clip starts
        endTime: videoClipDuration, // Use full video clip duration
        videoOffset: 0, // How much video content has been trimmed from the start (in seconds)
        originalStart: timelineItem.type === 'intro' || timelineItem.type === 'outro' ? 0 : timelineItem.startTime, // Original video file start time
        originalEnd: timelineItem.type === 'intro' || timelineItem.type === 'outro' ? videoClipDuration : timelineItem.endTime, // Original video file end time
        originalDuration: videoClipDuration, // Original clip duration before any trimming
        source: 'video'
      };
      setVideoClips([videoClip]);
      
      // Initialize audio clip (only if audio exists or language-specific audio exists)
      if (timelineItem.scene_audio || languageSpecificAudioPath) {
        
        // Test if language-specific audio file exists before setting up audio clip
        const testAudioPath = `${API_URL}${languageSpecificAudioPath}`;
        fetch(testAudioPath, { method: 'HEAD' })
          .then(response => {
            if (response.ok) {
              
              // Create temporary audio element to get actual duration
              const tempAudio = new Audio(`${API_URL}${languageSpecificAudioPath}`);
              tempAudio.addEventListener('loadedmetadata', () => {
                const audioDuration = tempAudio.duration;
                setActualAudioDuration(audioDuration);
                
                // Use the longer duration between video and audio for timeline
                const finalDuration = Math.max(videoClipDuration, audioDuration);
                
                // Only set duration if configuration hasn't been loaded yet (ref-based check)
                if (!configurationLoadedRef.current) {
                  setDurationWithLogging(finalDuration, 'timeline-initialization-audio-metadata');
                } else {
                }
                
                // Set up audio clip with full duration
                setAudioClips([{
                  id: 'audio-1',
                  type: 'audio',
                  name: 'Audio',
                  startTime: 0,
                  endTime: audioDuration, // Use actual audio duration
                  audioOffset: 0, // How much audio content has been trimmed from the start
                  originalStart: timelineItem.startTime,
                  originalEnd: timelineItem.endTime,
                  source: languageSpecificAudioPath,
                  audioPath: languageSpecificAudioPath,
                  actualDuration: audioDuration
                }]);
                
                // Update video clip end time if audio is longer
                if (audioDuration > videoClipDuration) {
                  // Don't constrain video clip - allow it to extend to match timeline duration
                  // The video will show its last frame for the extended duration
                }
              });
              
              tempAudio.addEventListener('error', (e) => {
                // Fallback to video duration
                setActualAudioDuration(videoClipDuration);
                setAudioClips([{
                  id: 'audio-1',
                  type: 'audio',
                  name: 'Audio',
                  startTime: 0,
                  endTime: videoClipDuration,
                  audioOffset: 0,
                  originalStart: timelineItem.startTime,
                  originalEnd: timelineItem.endTime,
                  source: languageSpecificAudioPath,
                  audioPath: languageSpecificAudioPath
                }]);
              });
            } else {
              setAudioClips([]);
            }
          })
          .catch(error => {
            // If the check fails, still try to set up the audio clip
            // The audio element will handle the error if the file doesn't exist
            
            // Create temporary audio element to get actual duration even on error
            const tempAudio = new Audio(`${API_URL}${languageSpecificAudioPath}`);
            tempAudio.addEventListener('loadedmetadata', () => {
              const audioDuration = tempAudio.duration;
              setActualAudioDuration(audioDuration);
              
              const finalDuration = Math.max(videoClipDuration, audioDuration);
              
              // Only set duration if no custom configuration is loaded
              // Also check if duration was already set from configuration
              const wasLoadedFromConfig = duration > 0 && duration !== videoClipDuration && duration !== finalDuration;
              if ((!hasCustomConfig || !configurationLoaded) && !wasLoadedFromConfig) {
                setDurationWithLogging(finalDuration, 'timeline-initialization-audio-fallback');
              } else {
              }
            });
            
            setAudioClips([{
              id: 'audio-1',
              type: 'audio',
              name: 'Audio',
              startTime: 0,
              endTime: videoClipDuration, // Will be updated when we get actual duration
              audioOffset: 0, // How much audio content has been trimmed from the start
              originalStart: timelineItem.startTime,
              originalEnd: timelineItem.endTime,
              source: languageSpecificAudioPath,
              audioPath: languageSpecificAudioPath
            }]);
          });
      } else {
        setAudioClips([]);
      }
    }
  }, [timelineItem, currentLanguage, itemId, configurationLoaded]);

  // Calculate canvas scaling for display
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 20; // Try for up to 2 seconds
    
    const calculateCanvasScale = () => {
      if (!canvasContainerRef.current) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(calculateCanvasScale, 100);
          return;
        } else {
          // Set default values to prevent blocking
          setCanvasScale(0.5);
          setCanvasReady(true);
          return;
        }
      }
      
      const container = canvasContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      // Ensure we have valid dimensions
      if (containerRect.width === 0 || containerRect.height === 0) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(calculateCanvasScale, 100);
          return;
        }
      }
      
      // Use much more conservative sizing for comfortable viewing
      const availableWidth = Math.max(containerRect.width * 0.6, 400); // Use 60% of container width, minimum 400px
      const availableHeight = Math.max(containerRect.height * 0.6, 300); // Use 60% of container height, minimum 300px
      
      // Calculate scale to fit 1920x1080 canvas in available space
      const scaleX = availableWidth / 1920;
      const scaleY = availableHeight / 1080;
      const displayScale = Math.max(Math.min(scaleX, scaleY, 0.375), 0.1); // Cap at 37.5% max for comfortable viewing, minimum 0.1
      
      
      setCanvasScale(displayScale);
      setCanvasReady(true);
      
      // Store the original fit scale for the "Fit to Screen" button
      if (originalFitScale === null) {
        setOriginalFitScale(displayScale);
      }
      
      // Update scrollbar visibility after scale change
      setTimeout(() => {
        if (canvasContainerRef.current) {
          const container = canvasContainerRef.current;
          const scaledCanvasWidth = 1920 * displayScale;
          const scaledCanvasHeight = 1080 * displayScale;
          
          const needsHorizontalScroll = scaledCanvasWidth > container.clientWidth;
          const needsVerticalScroll = scaledCanvasHeight > container.clientHeight;
          
          if (needsHorizontalScroll || needsVerticalScroll) {
            container.style.overflow = 'auto';
          } else {
            container.style.overflow = 'hidden';
          }
        }
      }, 100);
      
      // Force a redraw once canvas is ready
      setForceRedraw(prev => prev + 1);
      
    };
    
    // Function to check if scrollbars are needed
    const updateScrollbarVisibility = () => {
      if (canvasContainerRef.current) {
        const container = canvasContainerRef.current;
        const scaledCanvasWidth = 1920 * canvasScale;
        const scaledCanvasHeight = 1080 * canvasScale;
        
        const needsHorizontalScroll = scaledCanvasWidth > container.clientWidth;
        const needsVerticalScroll = scaledCanvasHeight > container.clientHeight;
        
        if (needsHorizontalScroll || needsVerticalScroll) {
          container.style.overflow = 'auto';
        } else {
          container.style.overflow = 'hidden';
        }
      }
    };
    
    // Start immediately, then retry if needed
    calculateCanvasScale();
    
    // Also calculate on resize
    const handleResize = () => {
      retryCount = 0; // Reset retry count on resize
      calculateCanvasScale();
      setTimeout(updateScrollbarVisibility, 100);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Ensure canvas element is ready and force redraw when needed
  useEffect(() => {
    if (canvasReady && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        setForceRedraw(prev => prev + 1);
      }
    }
  }, [canvasReady]);

  // Coordinate conversion utilities for canvas
  const screenToCanvasCoords = useCallback((screenX, screenY) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Convert screen coordinates to canvas-relative coordinates
    const relativeX = screenX - rect.left;
    const relativeY = screenY - rect.top;
    
    // Convert from scaled display coordinates to actual canvas coordinates (1920x1080)
    const canvasX = relativeX / canvasScale;
    const canvasY = relativeY / canvasScale;
    
    return { x: canvasX, y: canvasY };
  }, [canvasScale]);

  // Update scrollbar visibility whenever canvas scale changes (from zoom buttons)
  useEffect(() => {
    const updateScrollbars = () => {
      if (canvasContainerRef.current && canvasReady) {
        const container = canvasContainerRef.current;
        const scaledCanvasWidth = 1920 * canvasScale;
        const scaledCanvasHeight = 1080 * canvasScale;
        
        const needsHorizontalScroll = scaledCanvasWidth > container.clientWidth;
        const needsVerticalScroll = scaledCanvasHeight > container.clientHeight;
        
        if (needsHorizontalScroll || needsVerticalScroll) {
          container.style.overflow = 'auto';
        } else {
          container.style.overflow = 'hidden';
        }
      }
    };

    // Small delay to ensure canvas has rendered at new scale
    const timeoutId = setTimeout(updateScrollbars, 50);
    return () => clearTimeout(timeoutId);
  }, [canvasScale, canvasReady]);
  
  // Ensure audio element loads when audioClips change
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && audioClips.length > 0 && audioClips[0].audioPath) {
      audio.load(); // Force reload of audio element
      
      // Try to preload the audio
      const preloadHandler = () => {
      };
      const errorHandler = (e) => {
      };
      
      audio.addEventListener('canplaythrough', preloadHandler, { once: true });
      audio.addEventListener('error', errorHandler, { once: true });
      
      return () => {
        audio.removeEventListener('canplaythrough', preloadHandler);
        audio.removeEventListener('error', errorHandler);
      };
    }
  }, [audioClips]);
  
  // Fetch timeline item and project data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch timeline item
        let itemData = null;
        const itemResponse = await fetch(`${API_URL}/timeline-items/${itemId}`);
        if (itemResponse.ok) {
          itemData = await itemResponse.json();
          setTimelineItem(itemData);
        }
        
        // Fetch project data and timeline items
        const [projectResponse, timelineResponse] = await Promise.all([
          fetch(`${API_URL}/projects/${projectId}`),
          fetch(`${API_URL}/projects/${projectId}/timeline-items`)
        ]);
        
        if (projectResponse.ok) {
          const projectData = await projectResponse.json();
          
          // Override video URL for intro/outro items
          if (itemData && (itemData.type === 'intro' || itemData.type === 'outro')) {
            const mediaFileName = itemData.type === 'intro' ? 'intro.mp4' : 'outro.mp4';
            projectData.videoUrl = `/media/${mediaFileName}`;
          } else {
            // Handle multi-video projects - use the specific video for this clip
            if (projectData.videoUrl && typeof projectData.videoUrl === 'string') {
              try {
                const parsed = JSON.parse(projectData.videoUrl);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  // Check if this clip has a specific video reference
                  console.log('🔍 DEBUG: Video selection for clip:', {
                    itemData_video_url: itemData?.video_url,
                    itemData_video_index: itemData?.video_index,
                    video_index_type: typeof itemData?.video_index,
                    parsed_array: parsed,
                    parsed_at_index: parsed[itemData?.video_index]
                  });

                  if (itemData && itemData.video_url) {
                    // Use the video specified in the clip
                    projectData.videoUrl = itemData.video_url;
                    console.log('🎬 ClipEditor: Using clip-specific video:', projectData.videoUrl);
                  } else if (itemData && typeof itemData.video_index === 'number' && itemData.video_index >= 0 && itemData.video_index < parsed.length) {
                    // Use video by index - fixed condition to handle video_index 0
                    projectData.videoUrl = parsed[itemData.video_index];
                    console.log('🎬 ClipEditor: Using video at index', itemData.video_index, ':', projectData.videoUrl);
                  } else {
                    // Fallback to first video
                    projectData.videoUrl = parsed[0];
                    console.log('🎬 ClipEditor: Fallback to first video:', projectData.videoUrl);
                    console.log('⚠️ DEBUG: Fallback reason:', {
                      has_itemData: !!itemData,
                      video_index: itemData?.video_index,
                      video_index_is_number: typeof itemData?.video_index === 'number',
                      video_at_index_exists: !!parsed[itemData?.video_index],
                      parsed_length: parsed.length
                    });
                  }
                }
              } catch (e) {
                // Not JSON, keep as is (backward compatibility)
                console.log('🎬 ClipEditor: Using single video URL:', projectData.videoUrl);
              }
            }
          }
          
          setProject(projectData);
        }
        
        // Process timeline items and calculate item index
        if (timelineResponse.ok) {
          const allTimelineItems = await timelineResponse.json();
          setTimelineItems(allTimelineItems);
          
          // Calculate the current item's index (1-based)
          const currentIndex = allTimelineItems.findIndex(item => item.id === parseInt(itemId));
          if (currentIndex !== -1) {
            setItemIndex(currentIndex + 1);
          }
        }
      } catch (error) {
        navigate(`/project/${projectId}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (itemId && projectId) {
      fetchData();
    }
  }, [itemId, projectId, navigate]);
  
  // Initialize script data when timeline item is loaded
  useEffect(() => {
    if (timelineItem) {
      // Get the appropriate narration text based on current language
      if (currentLanguage === 'original') {
        // Use scene_description if available, otherwise fall back to rawText
        const description = timelineItem.scene_description || timelineItem.rawText || '';
        setScriptDescription(description);
        setScriptNarration(timelineItem.scene_narration || '');
      } else {
        // For alternate languages, check translations
        const translation = timelineItem.translations?.find(t => t.language_code === currentLanguage);
        // Use scene_description if available, otherwise fall back to rawText
        const description = timelineItem.scene_description || timelineItem.rawText || '';
        setScriptDescription(description);
        setScriptNarration(translation?.translated_text || timelineItem.scene_narration || '');
      }
    }
  }, [timelineItem, currentLanguage]);
  
  // Initialize default timeline (extracted from useEffect logic)
  const initializeDefaultTimeline = async () => {
    if (!timelineItem) return;
    
    
    // Calculate initial video clip duration
    const videoClipDuration = (timelineItem.endTime || 0) - (timelineItem.startTime || 0);
    
    // Construct language-specific audio path at the beginning of the effect  
    const languageSpecificAudioPath = currentLanguage === 'original' 
      ? timelineItem.scene_audio
      : `/audio/timeline_item_audio_${itemId}_${currentLanguage}.mp3`;
    
    
    // Set initial duration to video clip duration, will be updated when we get actual audio duration
    setDuration(videoClipDuration);
    setActualVideoDuration(videoClipDuration);
    
    // Initialize video clip - start at position 0 seconds
    setVideoClips([{
      id: 'video-1',
      type: 'video',
      name: 'Video',
      startTime: 0.0, // Timeline position where this video clip starts
      endTime: videoClipDuration, // Use full video clip duration
      videoOffset: 0, // How much video content has been trimmed from the start (in seconds)
      originalStart: timelineItem.startTime, // Original video file start time
      originalEnd: timelineItem.endTime, // Original video file end time
      originalDuration: videoClipDuration, // Original clip duration before any trimming
      source: 'video'
    }]);
    
    // Initialize audio clip (only if audio exists or language-specific audio exists)
    if (timelineItem.scene_audio || languageSpecificAudioPath) {
      
      // Test if language-specific audio file exists before setting up audio clip
      const testAudioPath = `${API_URL}${languageSpecificAudioPath}`;
      try {
        const response = await fetch(testAudioPath, { method: 'HEAD' });
        if (response.ok) {
          
          // Create temporary audio element to get actual duration
          const tempAudio = new Audio(`${API_URL}${languageSpecificAudioPath}`);
          tempAudio.addEventListener('loadedmetadata', () => {
            const audioDuration = tempAudio.duration;
            setActualAudioDuration(audioDuration);
            
            // Use the longer duration between video and audio for timeline
            const finalDuration = Math.max(videoClipDuration, audioDuration);
            setDuration(finalDuration);
            
            // Set up audio clip with full duration
            setAudioClips([{
              id: 'audio-1',
              type: 'audio',
              name: 'Audio',
              startTime: 0,
              endTime: audioDuration, // Use actual audio duration
              audioOffset: 0, // How much audio content has been trimmed from the start
              originalStart: timelineItem.startTime,
              originalEnd: timelineItem.endTime,
              source: languageSpecificAudioPath,
              audioPath: languageSpecificAudioPath,
              actualDuration: audioDuration
            }]);
            
            // Update video clip end time if audio is longer
            if (audioDuration > videoClipDuration) {
              // Don't constrain video clip - allow it to extend to match timeline duration
              // The video will show its last frame for the extended duration
            }
          });
          
          tempAudio.addEventListener('error', (e) => {
            // Fallback to video duration
            setActualAudioDuration(videoClipDuration);
            setAudioClips([{
              id: 'audio-1',
              type: 'audio',
              name: 'Audio',
              startTime: 0,
              endTime: videoClipDuration,
              audioOffset: 0,
              originalStart: timelineItem.startTime,
              originalEnd: timelineItem.endTime,
              source: languageSpecificAudioPath,
              audioPath: languageSpecificAudioPath
            }]);
          });
        } else {
          setAudioClips([]);
        }
      } catch (error) {
        // If the check fails, still try to set up the audio clip
        // The audio element will handle the error if the file doesn't exist
        
        // Create temporary audio element to get actual duration even on error
        const tempAudio = new Audio(`${API_URL}${languageSpecificAudioPath}`);
        tempAudio.addEventListener('loadedmetadata', () => {
          const audioDuration = tempAudio.duration;
          setActualAudioDuration(audioDuration);
          
          const finalDuration = Math.max(videoClipDuration, audioDuration);
          setDuration(finalDuration);
        });
        
        setAudioClips([{
          id: 'audio-1',
          type: 'audio',
          name: 'Audio',
          startTime: 0,
          endTime: videoClipDuration, // Will be updated when we get actual duration
          audioOffset: 0, // How much audio content has been trimmed from the start
          originalStart: timelineItem.startTime,
          originalEnd: timelineItem.endTime,
          source: languageSpecificAudioPath,
          audioPath: languageSpecificAudioPath
        }]);
      }
    } else {
      setAudioClips([]);
    }
  };

  // Load clip configuration for current language
  const loadClipConfiguration = async () => {
    try {
      const response = await fetch(`${API_URL}/timeline-items/${itemId}/clip-config?language_code=${currentLanguage}`);
      
      if (response.ok) {
        const configData = await response.json();

        setHasCustomConfig(configData.has_custom_config);
        setConfigurationLoaded(true);
        configurationLoadedRef.current = true;
        
        if (configData.configuration && configData.configuration.timeline) {
          const timeline = configData.configuration.timeline;
          
          // Load video clips from configuration
          if (timeline.videoTrack) {
            setVideoClips(timeline.videoTrack);
          }
          
          // Load audio clips from configuration
          if (timeline.audioTrack) {
            // Update audio path for current language if needed
            const audioClipsWithLanguage = timeline.audioTrack.map(clip => ({
              ...clip,
              audioPath: clip.audioPath || `/audio/timeline_item_audio_${itemId}_${currentLanguage === 'original' ? 'original' : currentLanguage}.mp3`
            }));
            setAudioClips(audioClipsWithLanguage);
          }
          
          // Load shape layers from configuration
          if (timeline.shapeLayers) {
            setShapeLayers(timeline.shapeLayers);
          }
          
          // Load text layers from configuration
          if (timeline.textLayers) {
            setTextLayers(timeline.textLayers);
          }
          
          
          // Set timeline duration
          if (timeline.duration) {
            setDurationWithLogging(timeline.duration, 'configuration-loading');
            setCustomDuration(timeline.duration); // Also update the input field
          }
        } else {
          // No saved configuration - initialize with default timeline
          
          // Initialize default video clip covering the full timeline
          let videoClipDuration;
          if (timelineItem.type === 'intro' || timelineItem.type === 'outro') {
            // For intro/outro items, use a default duration since they don't have startTime/endTime
            videoClipDuration = 10.0; // Default 10 seconds, will be updated when video loads
          } else {
            videoClipDuration = timelineItem.endTime - timelineItem.startTime;
          }
          const defaultVideoClip = {
            id: 'video-1',
            type: 'video',
            name: 'Video',
            startTime: 0,
            endTime: videoClipDuration,
            duration: videoClipDuration,
            videoOffset: timelineItem.type === 'intro' || timelineItem.type === 'outro' ? 0 : timelineItem.startTime, // Start from timeline item's video position
            source: 'video'
          };
          
          setVideoClips([defaultVideoClip]);
          
          // Set timeline duration
          setDuration(videoClipDuration);
        }
      } else {
        setHasCustomConfig(false);
        setConfigurationLoaded(true);
        
        // Initialize default video clip covering the full timeline
        let videoClipDuration;
        if (timelineItem.type === 'intro' || timelineItem.type === 'outro') {
          // For intro/outro items, use a default duration since they don't have startTime/endTime
          videoClipDuration = 10.0; // Default 10 seconds, will be updated when video loads
        } else {
          videoClipDuration = timelineItem.endTime - timelineItem.startTime;
        }
        const defaultVideoClip = {
          id: 'video-1',
          type: 'video',
          name: 'Video',
          startTime: 0,
          endTime: videoClipDuration,
          duration: videoClipDuration,
          videoOffset: timelineItem.type === 'intro' || timelineItem.type === 'outro' ? 0 : timelineItem.startTime, // Start from timeline item's video position
          source: 'video'
        };
        
        setVideoClips([defaultVideoClip]);
        
        // Set timeline duration
        setDuration(videoClipDuration);
        
        // Clear any existing shape and text elements when no configuration exists
        setShapeLayers([]);
        setTextLayers([]);
      }
    } catch (error) {
      setHasCustomConfig(false);
      setConfigurationLoaded(true);
      
      // Initialize default video clip even when config loading fails
      let videoClipDuration;
      if (timelineItem.type === 'intro' || timelineItem.type === 'outro') {
        // For intro/outro items, use a default duration since they don't have startTime/endTime
        videoClipDuration = 10.0; // Default 10 seconds, will be updated when video loads
      } else {
        videoClipDuration = timelineItem.endTime - timelineItem.startTime;
      }
      const defaultVideoClip = {
        id: 'video-1',
        type: 'video',
        name: 'Video',
        startTime: 0,
        endTime: videoClipDuration,
        duration: videoClipDuration,
        videoOffset: timelineItem.type === 'intro' || timelineItem.type === 'outro' ? 0 : timelineItem.startTime,
        source: 'video'
      };
      
      setVideoClips([defaultVideoClip]);
      
      setDuration(videoClipDuration);
      
      // Clear any existing shape and text elements when configuration loading fails
      setShapeLayers([]);
      setTextLayers([]);
    }
  };
  
  // Show reset confirmation modal
  const showResetConfirmation = () => {
    setShowResetConfirm(true);
  };

  // Reset timeline by deleting the configuration from database
  const resetTimeline = async () => {
    try {
      
      // Delete the configuration from the database
      const response = await fetch(`${API_URL}/timeline-items/${itemId}/clip-config/${currentLanguage}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Update local state to reflect the deletion
        setHasCustomConfig(false);
        
        // Close confirmation modal
        setShowResetConfirm(false);
        
      } else {
        alert('Failed to reset timeline configuration');
      }
      
    } catch (error) {
      alert(`Failed to reset timeline: ${error.message}`);
    }
  };

  // Cancel reset confirmation
  const cancelReset = () => {
    setShowResetConfirm(false);
  };

  // Handle duration change
  const handleDurationChange = (newDuration) => {
    const parsedDuration = parseFloat(newDuration);
    if (parsedDuration > 0) {
      setDuration(parsedDuration);
      setCustomDuration(parsedDuration);
      
      // Reset playback to ensure it doesn't exceed new duration
      if (currentTime > parsedDuration) {
        setCurrentTime(0);
      }
    }
  };


  // Reset duration to original
  const resetDurationToOriginal = () => {
    const originalDuration = Math.max(actualVideoDuration || 0, actualAudioDuration || 0);
    if (originalDuration > 0) {
      handleDurationChange(originalDuration);
    }
  };

  // Add new shape layer with specific type
  const handleAddShape = (shapeType = 'rectangle') => {
    const shapeId = `shape-${Date.now()}`;
    const minDuration = 2.0; // 2 seconds minimum
    const fadeInDuration = 0.5;
    const fadeOutDuration = 0.5;
    const holdDuration = minDuration - fadeInDuration - fadeOutDuration; // 1 second hold
    
    // Place shape at current playback time, or at 0 if playing
    const startTime = Math.max(0, Math.min(currentTime, duration - minDuration));
    const endTime = Math.min(startTime + minDuration, duration);
    
    // Shape dimensions based on type
    let shapeWidth, shapeHeight, shapeName;
    switch (shapeType) {
      case 'circle':
        shapeWidth = 180;
        shapeHeight = 180;
        shapeName = 'Circle';
        break;
      case 'arrow':
        shapeWidth = 280;
        shapeHeight = 120;
        shapeName = 'Arrow';
        break;
      case 'rectangle':
      default:
        shapeWidth = 200;
        shapeHeight = 150;
        shapeName = 'Rectangle';
        break;
    }
    
    // Fixed canvas dimensions (1920x1080) - center the shape
    const canvasWidth = 1920;
    const canvasHeight = 1080;
    
    const newShape = {
      id: shapeId,
      type: 'shape',
      shapeType: shapeType,
      name: shapeName,
      startTime: startTime,
      endTime: endTime,
      duration: endTime - startTime,
      // Visual properties - centered in fixed 1920x1080 canvas
      x: (canvasWidth - shapeWidth) / 2,  // 860px from left (absolute center)
      y: (canvasHeight - shapeHeight) / 2, // 465px from top (absolute center)
      width: shapeWidth,
      height: shapeHeight,
      rotation: 0, // Degrees
      scaleX: 1,
      scaleY: 1,
      // Style properties based on mode
      mode: shapeMode,
      fill: shapeMode === 'fill' ? '#ff0000' : 'transparent',
      stroke: shapeMode === 'outline' ? '#ff0000' : 'transparent',
      strokeWidth: shapeMode === 'outline' ? 3 : 0,
      opacity: 1,
      // Animation properties - only add fade effects for non-blur modes
      ...(shapeMode !== 'blur' ? {
        fadeIn: fadeInDuration,
        fadeOut: fadeOutDuration,
        fadeInDuration: fadeInDuration,
        fadeOutDuration: fadeOutDuration,
        holdDuration: holdDuration,
      } : {}),
      // Metadata
      created_at: new Date().toISOString()
    };
    
    setShapeLayers(layers => [...layers, newShape]);
    setSelectedShapeId(shapeId); // Select the new shape
    
  };

  // Add new text layer
  const handleAddText = () => {
    const textId = `text-${Date.now()}`;
    const minDuration = 2.0; // 2 seconds minimum
    const fadeInDuration = 0.5;
    const fadeOutDuration = 0.5;
    const holdDuration = minDuration - fadeInDuration - fadeOutDuration; // 1 second hold
    
    // Place text at current playback time, or at 0 if playing
    const startTime = Math.max(0, Math.min(currentTime, duration - minDuration));
    const endTime = Math.min(startTime + minDuration, duration);
    
    // Fixed canvas dimensions (1920x1080) - center the text
    const canvasWidth = 1920;
    const canvasHeight = 1080;
    
    const newText = {
      id: textId,
      type: 'text',
      text: 'Text',
      name: 'Text Layer',
      startTime: startTime,
      endTime: endTime,
      duration: endTime - startTime,
      // Position - left edge positioned in fixed 1920x1080 canvas
      x: (canvasWidth - 200) / 2,  // Left edge of 200px wide text box, centered
      y: canvasHeight / 2, // 540px from top (vertical center)
      width: 200, // Initial text width
      // Text styling - Arial Bold as specified
      fontSize: 48,
      fontFamily: 'Arial',
      fontStyle: 'bold',
      fill: '#ff0000', // Red text
      align: 'left',
      // Transform properties
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      // Animation properties
      fadeInDuration: fadeInDuration,
      fadeOutDuration: fadeOutDuration,
      holdDuration: holdDuration,
      // Timeline properties
      padding: 5,
      // Metadata
      created_at: new Date().toISOString()
    };
    
    setTextLayers(layers => [...layers, newText]);
    setSelectedTextId(textId); // Select the new text
    
  };

  // Handle shape layer drag on timeline
  const handleShapeDrag = useCallback((shapeId, newStartTime) => {
    setShapeLayers(layers => layers.map(layer => 
      layer.id === shapeId 
        ? { ...layer, startTime: newStartTime, endTime: newStartTime + layer.duration }
        : layer
    ));
  }, []);

  // Handle shape layer resize on timeline  
  const handleShapeResize = useCallback((shapeId, edge, newTime) => {
    const minDuration = 0.5; // Minimum 0.5 seconds (allowing for fade in/out)
    
    setShapeLayers(layers => layers.map(layer => {
      if (layer.id === shapeId) {
        if (edge === 'start') {
          const maxStartTime = layer.endTime - minDuration;
          const clampedStartTime = Math.max(0, Math.min(newTime, maxStartTime));
          const newDuration = layer.endTime - clampedStartTime;
          
          // Recalculate fade timing but keep fade durations fixed
          const fadeInDuration = 0.5;
          const fadeOutDuration = 0.5;
          const holdDuration = Math.max(0, newDuration - fadeInDuration - fadeOutDuration);
          
          return { 
            ...layer, 
            startTime: clampedStartTime,
            duration: newDuration,
            holdDuration: holdDuration
          };
        } else {
          // Right edge resize
          const minEndTime = layer.startTime + minDuration;
          const clampedEndTime = Math.max(minEndTime, Math.min(newTime, duration));
          const newDuration = clampedEndTime - layer.startTime;
          
          // Recalculate fade timing but keep fade durations fixed
          const fadeInDuration = 0.5;
          const fadeOutDuration = 0.5;
          const holdDuration = Math.max(0, newDuration - fadeInDuration - fadeOutDuration);
          
          return { 
            ...layer, 
            endTime: clampedEndTime,
            duration: newDuration,
            holdDuration: holdDuration
          };
        }
      }
      return layer;
    }));
  }, [duration]);

  // Handle text layer drag on timeline
  const handleTextDrag = useCallback((textId, newStartTime) => {
    setTextLayers(layers => layers.map(layer => 
      layer.id === textId 
        ? { ...layer, startTime: newStartTime, endTime: newStartTime + layer.duration }
        : layer
    ));
  }, []);

  // Handle text layer resize on timeline  
  const handleTextResize = useCallback((textId, edge, newTime) => {
    const minDuration = 0.5; // Minimum 0.5 seconds
    
    setTextLayers(layers => layers.map(layer => {
      if (layer.id === textId) {
        if (edge === 'start') {
          const maxStartTime = layer.endTime - minDuration;
          const clampedStartTime = Math.max(0, Math.min(newTime, maxStartTime));
          const newDuration = layer.endTime - clampedStartTime;
          
          return { 
            ...layer, 
            startTime: clampedStartTime,
            duration: newDuration
          };
        } else {
          // Right edge resize
          const minEndTime = layer.startTime + minDuration;
          const clampedEndTime = Math.max(minEndTime, Math.min(newTime, duration));
          const newDuration = clampedEndTime - layer.startTime;
          
          return { 
            ...layer, 
            endTime: clampedEndTime,
            duration: newDuration
          };
        }
      }
      return layer;
    }));
  }, [duration]);

  // Handle text deletion
  const handleTextDelete = useCallback((textId) => {
    setTextLayers(layers => {
      const filteredLayers = layers.filter(layer => layer.id !== textId);
      return filteredLayers;
    });
    if (selectedTextId === textId) {
      setSelectedTextId(null);
    }
    if (editingTextId === textId) {
      setEditingTextId(null);
    }
  }, [selectedTextId, editingTextId]);

  // Shape transformation handlers
  const [dragState, setDragState] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [rotateState, setRotateState] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  

  // Expose drag state setter globally for ClipBlock components
  useEffect(() => {
    window.setTimelineDragState = setIsDragging;
    return () => {
      delete window.setTimelineDragState;
    };
  }, []);

  // Handle shape drag/move
  const handleShapeMouseDown = useCallback((e, shapeId) => {
    if (e.target.classList.contains('transform-handle')) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    
    const shape = shapeLayers.find(s => s.id === shapeId);
    if (!shape) {
      return;
    }
    
    setDragState({
      shapeId,
      startX: e.clientX,
      startY: e.clientY,
      initialX: shape.x,
      initialY: shape.y
    });
    
    setSelectedShapeId(shapeId);
  }, [shapeLayers]);

  // Handle resize start
  const handleResizeStart = useCallback((e, shapeId, direction) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shape = shapeLayers.find(s => s.id === shapeId);
    if (!shape) return;
    
    setResizeState({
      shapeId,
      direction,
      startX: e.clientX,
      startY: e.clientY,
      initialWidth: shape.width,
      initialHeight: shape.height,
      initialX: shape.x,
      initialY: shape.y,
      initialScaleX: shape.scaleX,
      initialScaleY: shape.scaleY
    });
  }, [shapeLayers]);

  // Handle rotation start
  const handleRotateStart = useCallback((e, shapeId) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shape = shapeLayers.find(s => s.id === shapeId);
    if (!shape) return;
    
    const rect = e.currentTarget.closest('.shape-element').getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180 / Math.PI;
    
    setRotateState({
      shapeId,
      startAngle,
      initialRotation: shape.rotation,
      centerX,
      centerY
    });
  }, [shapeLayers]);

  // Mouse move handler for all transformations
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (dragState) {
        // Convert screen delta to canvas delta
        const screenDeltaX = e.clientX - dragState.startX;
        const screenDeltaY = e.clientY - dragState.startY;
        const canvasDeltaX = screenDeltaX / canvasScale;
        const canvasDeltaY = screenDeltaY / canvasScale;
        
        setShapeLayers(layers => layers.map(layer => 
          layer.id === dragState.shapeId 
            ? { ...layer, x: dragState.initialX + canvasDeltaX, y: dragState.initialY + canvasDeltaY }
            : layer
        ));
      } else if (resizeState) {
        // Convert screen delta to canvas delta for resize
        const screenDeltaX = e.clientX - resizeState.startX;
        const screenDeltaY = e.clientY - resizeState.startY;
        const deltaX = screenDeltaX / canvasScale;
        const deltaY = screenDeltaY / canvasScale;
        
        setShapeLayers(layers => layers.map(layer => {
          if (layer.id === resizeState.shapeId) {
            let newWidth = resizeState.initialWidth;
            let newHeight = resizeState.initialHeight;
            let newX = resizeState.initialX;
            let newY = resizeState.initialY;
            
            const minSize = 20; // Minimum size
            
            switch (resizeState.direction) {
              case 'nw':
                newWidth = Math.max(minSize, resizeState.initialWidth - deltaX);
                newHeight = Math.max(minSize, resizeState.initialHeight - deltaY);
                newX = resizeState.initialX + (resizeState.initialWidth - newWidth);
                newY = resizeState.initialY + (resizeState.initialHeight - newHeight);
                break;
              case 'ne':
                newWidth = Math.max(minSize, resizeState.initialWidth + deltaX);
                newHeight = Math.max(minSize, resizeState.initialHeight - deltaY);
                newY = resizeState.initialY + (resizeState.initialHeight - newHeight);
                break;
              case 'sw':
                newWidth = Math.max(minSize, resizeState.initialWidth - deltaX);
                newHeight = Math.max(minSize, resizeState.initialHeight + deltaY);
                newX = resizeState.initialX + (resizeState.initialWidth - newWidth);
                break;
              case 'se':
                newWidth = Math.max(minSize, resizeState.initialWidth + deltaX);
                newHeight = Math.max(minSize, resizeState.initialHeight + deltaY);
                break;
              case 'n':
                newHeight = Math.max(minSize, resizeState.initialHeight - deltaY);
                newY = resizeState.initialY + (resizeState.initialHeight - newHeight);
                break;
              case 's':
                newHeight = Math.max(minSize, resizeState.initialHeight + deltaY);
                break;
              case 'w':
                newWidth = Math.max(minSize, resizeState.initialWidth - deltaX);
                newX = resizeState.initialX + (resizeState.initialWidth - newWidth);
                break;
              case 'e':
                newWidth = Math.max(minSize, resizeState.initialWidth + deltaX);
                break;
            }
            
            return { ...layer, width: newWidth, height: newHeight, x: newX, y: newY };
          }
          return layer;
        }));
      } else if (rotateState) {
        const currentAngle = Math.atan2(e.clientY - rotateState.centerY, e.clientX - rotateState.centerX) * 180 / Math.PI;
        const deltaAngle = currentAngle - rotateState.startAngle;
        const newRotation = rotateState.initialRotation + deltaAngle;
        
        setShapeLayers(layers => layers.map(layer => 
          layer.id === rotateState.shapeId 
            ? { ...layer, rotation: newRotation }
            : layer
        ));
      }
    };

    const handleMouseUp = () => {
      setDragState(null);
      setResizeState(null);
      setRotateState(null);
    };

    if (dragState || resizeState || rotateState) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, resizeState, rotateState]);





  // Click outside to deselect shape and close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't deselect if we're currently dragging/transforming shapes
      if (dragState || resizeState || rotateState) {
        return;
      }
      
      const shouldDeselect = !e.target.closest('.mode-selector-container') &&
          !e.target.closest('.font-size-selector-container') &&
          !e.target.closest('.shape-selector-container') &&
          !e.target.closest('[data-color-picker]') &&
          !e.target.closest('.timeline-container') &&
          !e.target.closest('.timeline-scrollable') &&
          !e.target.closest('.timeline-content') &&
          !e.target.closest('canvas'); // Include canvas elements (Konva)
      
      if (shouldDeselect) {
        setSelectedShapeId(null);
        setSelectedTextId(null);
      }
      if (!e.target.closest('.mode-selector-container')) {
        setShowModeDropdown(false);
      }
      if (!e.target.closest('.font-size-selector-container')) {
        setShowFontSizeDropdown(false);
      }
      if (!e.target.closest('.shape-selector-container')) {
        setShowShapeDropdown(false);
      }

      // Close extract frame dropdown
      if (!e.target.closest('.extract-frame-container')) {
        setShowExtractFrameDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dragState, resizeState, rotateState]);

  // Removed blur video synchronization - using CSS backdrop filter instead

  // Context menu handlers for Ripple Delete
  const handleRippleDeleteRequest = useCallback((trackType, gapStart, gapEnd, clipsToMove, pointer) => {
    const gapDuration = gapEnd - gapStart;
    
    setContextMenu({
      isVisible: true,
      x: pointer.x,
      y: pointer.y,
      trackType,
      gapStart,
      gapEnd,
      clipsToMove,
      gapDuration
    });
  }, []);

  const handleRippleDelete = useCallback(() => {
    const { trackType, gapStart, gapEnd, clipsToMove, gapDuration } = contextMenu;
    
    if (trackType === 'video') {
      // Move video clips
      setVideoClips(clips => 
        clips.map(clip => 
          clipsToMove.some(moveClip => moveClip.id === clip.id)
            ? { ...clip, startTime: clip.startTime - gapDuration, endTime: clip.endTime - gapDuration }
            : clip
        )
      );
    } else if (trackType === 'audio') {
      // Move audio clips  
      setAudioClips(clips => 
        clips.map(clip => 
          clipsToMove.some(moveClip => moveClip.id === clip.id)
            ? { ...clip, startTime: clip.startTime - gapDuration, endTime: clip.endTime - gapDuration }
            : clip
        )
      );
    }
    
    // Close context menu
    setContextMenu(prev => ({ ...prev, isVisible: false }));
    
  }, [contextMenu]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isVisible: false }));
  }, []);

  // Layer context menu handlers
  const handleLayerRightClick = useCallback((e, layerType, layerId, layer) => {
    // Handle both regular DOM events and Konva events
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }

    // Get the timeline container to calculate relative position
    const timelineContainer = document.querySelector('.timeline-container');
    if (!timelineContainer) {
      return;
    }

    // Get the mouse position relative to the page
    const pageX = e.clientX || e.pageX || 0;
    const pageY = e.clientY || e.pageY || 0;

    // Get timeline container's position
    const containerRect = timelineContainer.getBoundingClientRect();

    // Calculate position relative to timeline container
    const relativeX = pageX - containerRect.left;
    const relativeY = pageY - containerRect.top;

    // Context menu dimensions (based on actual CSS and content)
    const menuHeight = 120; // Header + 2 items + separator + 1 item + padding
    const menuWidth = 160;  // minWidth from CSS
    const cursorOffset = 5; // Small offset from cursor

    // Check available space around the click point
    const spaceBelow = containerRect.height - relativeY;
    const spaceAbove = relativeY;
    const spaceRight = containerRect.width - relativeX;
    const spaceLeft = relativeX;

    // Determine Y position (above or below with cursor offset)
    let finalY = relativeY + cursorOffset; // Default: below cursor
    if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
      // Not enough space below, but enough above - position above cursor
      finalY = relativeY - menuHeight - cursorOffset;
    }

    // Determine X position (with cursor offset and boundary checking)
    let finalX = relativeX + cursorOffset; // Default: to the right of cursor
    if (spaceRight < menuWidth + cursorOffset && spaceLeft > menuWidth + cursorOffset) {
      // Not enough space to the right, but enough to the left
      finalX = relativeX - menuWidth - cursorOffset;
    } else if (spaceRight < menuWidth + cursorOffset && spaceLeft < menuWidth + cursorOffset) {
      // Not enough space on either side, center it horizontally
      finalX = Math.max(10, Math.min(containerRect.width - menuWidth - 10, relativeX - menuWidth / 2));
    }

    // Ensure menu doesn't go outside container boundaries
    finalX = Math.max(10, Math.min(containerRect.width - menuWidth - 10, finalX));
    finalY = Math.max(10, Math.min(containerRect.height - menuHeight - 10, finalY));

    setLayerContextMenu({
      isVisible: true,
      x: finalX,
      y: finalY,
      layerType,
      layerId,
      layer
    });
  }, []);

  const handleCloseLayerContextMenu = useCallback(() => {
    setLayerContextMenu(prev => ({ ...prev, isVisible: false }));
  }, []);

  // Extend layer to start (0 seconds)
  const handleExtendToStart = useCallback(() => {
    const { layerType, layerId } = layerContextMenu;

    if (layerType === 'shape') {
      setShapeLayers(layers => layers.map(layer =>
        layer.id === layerId
          ? { ...layer, startTime: 0, endTime: layer.endTime }
          : layer
      ));
    } else if (layerType === 'text') {
      setTextLayers(layers => layers.map(layer =>
        layer.id === layerId
          ? { ...layer, startTime: 0, endTime: layer.endTime }
          : layer
      ));
    }

    handleCloseLayerContextMenu();
  }, [layerContextMenu]);

  // Extend layer to end (timeline duration)
  const handleExtendToEnd = useCallback(() => {
    const { layerType, layerId } = layerContextMenu;

    if (layerType === 'shape') {
      setShapeLayers(layers => layers.map(layer =>
        layer.id === layerId
          ? { ...layer, startTime: layer.startTime, endTime: duration }
          : layer
      ));
    } else if (layerType === 'text') {
      setTextLayers(layers => layers.map(layer =>
        layer.id === layerId
          ? { ...layer, startTime: layer.startTime, endTime: duration }
          : layer
      ));
    }

    handleCloseLayerContextMenu();
  }, [layerContextMenu, duration]);

  // Duplicate layer to a new track
  const handleDuplicateLayer = useCallback(() => {
    const { layerType, layerId, layer } = layerContextMenu;

    if (layerType === 'shape') {
      const newShapeId = `shape-${Date.now()}`;
      const duplicatedShape = {
        ...layer,
        id: newShapeId,
        name: `${layer.name || 'Shape'} Copy`
      };

      setShapeLayers(layers => [...layers, duplicatedShape]);
      setSelectedShapeId(newShapeId);
    } else if (layerType === 'text') {
      const newTextId = `text-${Date.now()}`;
      const duplicatedText = {
        ...layer,
        id: newTextId,
        name: `${layer.name || 'Text'} Copy`
      };

      setTextLayers(layers => [...layers, duplicatedText]);
      setSelectedTextId(newTextId);
    }

    handleCloseLayerContextMenu();
  }, [layerContextMenu]);

  // Close context menus when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenu.isVisible && !e.target.closest('.ripple-delete-context-menu')) {
        handleCloseContextMenu();
      }
      if (layerContextMenu.isVisible && !e.target.closest('.layer-context-menu')) {
        handleCloseLayerContextMenu();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.isVisible, layerContextMenu.isVisible, handleCloseContextMenu, handleCloseLayerContextMenu]);

  // Close layer context menu when timeline scrolls
  useEffect(() => {
    const handleScroll = () => {
      if (layerContextMenu.isVisible) {
        handleCloseLayerContextMenu();
      }
    };

    // Add scroll listener to the timeline scrollable container
    const timelineScrollable = document.querySelector('.timeline-scrollable');
    if (timelineScrollable) {
      timelineScrollable.addEventListener('scroll', handleScroll);
      return () => timelineScrollable.removeEventListener('scroll', handleScroll);
    }
  }, [layerContextMenu.isVisible, handleCloseLayerContextMenu]);

  // Handle shape deletion
  const handleShapeDelete = useCallback((shapeId) => {
    setShapeLayers(layers => {
      const filteredLayers = layers.filter(layer => layer.id !== shapeId);
      return filteredLayers;
    });
    
    // Clear selection if deleted shape was selected
    if (selectedShapeId === shapeId) {
      setSelectedShapeId(null);
    }
    
  }, [selectedShapeId]);

  // Extract frame functionality - capture current frame and open Frame Editor with current shapes/text
  const handleCaptureFrame = useCallback(async () => {
    try {
      // Close the dropdown
      setShowExtractFrameDropdown(false);

      // Step 1: Calculate absolute video timestamp from timeline position
      let absoluteVideoTimestamp = currentTime;

      // For clip editors, we need to map timeline position to original video timestamp
      if (timelineItem && videoClips.length > 0) {
        const videoClip = videoClips[0]; // Main video clip

        // Timeline position maps to original video time
        if (videoClip.originalStart !== undefined) {
          // Calculate offset from timeline position to original video position
          const timelineOffset = currentTime - videoClip.startTime; // How far into the clip on timeline
          absoluteVideoTimestamp = videoClip.originalStart + timelineOffset;

          console.log('Timeline position:', currentTime);
          console.log('Video clip timeline start:', videoClip.startTime);
          console.log('Video clip original start:', videoClip.originalStart);
          console.log('Calculated absolute timestamp:', absoluteVideoTimestamp);
        }
      }

      // Step 2: Capture frame at the absolute video timestamp
      const response = await fetch(`${API_URL}/timeline-items/${itemId}/capture-frame?timestamp=${absoluteVideoTimestamp}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const captureResult = await response.json();
      console.log('Frame captured successfully:', captureResult);

      // Step 2: Extract current shapes and text layers
      const currentShapes = [];
      const currentTexts = [];

      // Get all visible shapes at current time
      shapeLayers.forEach(shape => {
        if (currentTime >= shape.startTime && currentTime <= shape.endTime) {
          // Use absolute coordinates and sizes directly (both editors use similar canvas sizes)
          const shapeData = {
            type: shape.mode === 'blur' ? 'rectangle' : shape.shapeType, // Use shapeType property
            x: shape.x, // Use absolute coordinates directly
            y: shape.y,
            // Always include size information
            width: shape.width,
            height: shape.height,
            // For circles, include both radius and width for consistency
            ...(shape.shapeType === 'circle' && { radius: shape.width / 2 }),
            fill: shape.mode === 'fill' ? shape.color : 'transparent',
            stroke: shape.mode === 'outline' ? shape.color : 'transparent',
            strokeWidth: shape.mode === 'outline' ? 4 : 0,
            ...(shape.mode === 'blur' && { isBlurMode: true, blurRadius: 25 }),
            // Use absolute coordinate system
            coordinateSystem: 'absolute'
          };
          currentShapes.push(shapeData);
        }
      });

      // Get all visible text layers at current time
      textLayers.forEach(textLayer => {
        if (currentTime >= textLayer.startTime && currentTime <= textLayer.endTime) {
          // Use absolute coordinates directly (both editors use similar canvas sizes)
          const textData = {
            text: textLayer.text,
            x: textLayer.x, // Use absolute coordinates directly
            y: textLayer.y,
            fontSize: textLayer.fontSize || 32,
            fontFamily: textLayer.fontFamily || 'Arial',
            fill: '#ff0000', // Always use red for text
            fontStyle: textLayer.fontWeight || 'bold',
            // Use absolute coordinate system
            coordinateSystem: 'absolute'
          };
          currentTexts.push(textData);
        }
      });

      // Step 3: Generate Frame Editor URL with captured frame ID and current shapes/text
      const frameEditorBaseUrl = `/project/${projectId}/frame-editor/${captureResult.timeline_item_id}`;
      const urlParams = new URLSearchParams();

      // Add shapes if any exist (always use plural form to avoid duplication)
      if (currentShapes.length > 0) {
        urlParams.set('shapes', JSON.stringify(currentShapes));
      }

      // Add texts if any exist (always use plural form to avoid duplication)
      if (currentTexts.length > 0) {
        urlParams.set('texts', JSON.stringify(currentTexts));
      }

      // Step 4: Open Frame Editor in new tab
      const finalUrl = urlParams.toString()
        ? `${frameEditorBaseUrl}?${urlParams.toString()}`
        : frameEditorBaseUrl;

      console.log('=== CAPTURE FRAME DEBUG ===');
      console.log('Opening Frame Editor with URL:', finalUrl);
      console.log('Total shapes found:', currentShapes.length);
      console.log('Total texts found:', currentTexts.length);

      currentShapes.forEach((shape, index) => {
        console.log(`Shape ${index}:`, {
          type: shape.type,
          coordinates: { x: shape.x, y: shape.y },
          size: { width: shape.width, height: shape.height, radius: shape.radius },
          coordinateSystem: shape.coordinateSystem,
          colors: { fill: shape.fill, stroke: shape.stroke },
          strokeWidth: shape.strokeWidth
        });
      });

      currentTexts.forEach((text, index) => {
        console.log(`Text ${index}:`, {
          text: text.text,
          coordinates: { x: text.x, y: text.y },
          fontSize: text.fontSize,
          fill: text.fill,
          coordinateSystem: text.coordinateSystem
        });
      });

      // Open in new tab
      window.open(finalUrl, '_blank');

    } catch (error) {
      console.error('Error capturing frame:', error);
      alert(`Failed to capture frame: ${error.message}`);
    }
  }, [itemId, currentTime, projectId, shapeLayers, textLayers, setShowExtractFrameDropdown, timelineItem, videoClips]);

  // Open Frame Editor with current shapes and text (without capturing new frame)
  const handleOpenFrameEditor = useCallback(() => {
    try {
      // Close the dropdown
      setShowExtractFrameDropdown(false);

      // Extract current shapes and text layers (same logic as capture frame)
      const currentShapes = [];
      const currentTexts = [];

      // Get all visible shapes at current time
      shapeLayers.forEach(shape => {
        if (currentTime >= shape.startTime && currentTime <= shape.endTime) {
          // Use absolute coordinates and sizes directly (both editors use similar canvas sizes)
          const shapeData = {
            type: shape.mode === 'blur' ? 'rectangle' : shape.shapeType, // Use shapeType property
            x: shape.x, // Use absolute coordinates directly
            y: shape.y,
            // Always include size information
            width: shape.width,
            height: shape.height,
            // For circles, include both radius and width for consistency
            ...(shape.shapeType === 'circle' && { radius: shape.width / 2 }),
            fill: shape.mode === 'fill' ? shape.color : 'transparent',
            stroke: shape.mode === 'outline' ? shape.color : 'transparent',
            strokeWidth: shape.mode === 'outline' ? 4 : 0,
            ...(shape.mode === 'blur' && { isBlurMode: true, blurRadius: 25 }),
            // Use absolute coordinate system
            coordinateSystem: 'absolute'
          };
          currentShapes.push(shapeData);
        }
      });

      // Get all visible text layers at current time
      textLayers.forEach(textLayer => {
        if (currentTime >= textLayer.startTime && currentTime <= textLayer.endTime) {
          // Use absolute coordinates directly (both editors use similar canvas sizes)
          const textData = {
            text: textLayer.text,
            x: textLayer.x, // Use absolute coordinates directly
            y: textLayer.y,
            fontSize: textLayer.fontSize || 32,
            fontFamily: textLayer.fontFamily || 'Arial',
            fill: '#ff0000', // Always use red for text
            fontStyle: textLayer.fontWeight || 'bold',
            // Use absolute coordinate system
            coordinateSystem: 'absolute'
          };
          currentTexts.push(textData);
        }
      });

      // Generate Frame Editor URL with current timeline item (existing screenshot)
      const frameEditorBaseUrl = `/project/${projectId}/frame-editor/${itemId}`;
      const urlParams = new URLSearchParams();

      // Add shapes if any exist (always use plural form to avoid duplication)
      if (currentShapes.length > 0) {
        urlParams.set('shapes', JSON.stringify(currentShapes));
      }

      // Add texts if any exist (always use plural form to avoid duplication)
      if (currentTexts.length > 0) {
        urlParams.set('texts', JSON.stringify(currentTexts));
      }

      // Open Frame Editor in new tab
      const finalUrl = urlParams.toString()
        ? `${frameEditorBaseUrl}?${urlParams.toString()}`
        : frameEditorBaseUrl;

      console.log('Opening Frame Editor with existing screenshot and current shapes/text:', finalUrl);
      console.log('Current shapes:', currentShapes);
      console.log('Current texts:', currentTexts);

      // Debug: Log coordinate conversion
      currentShapes.forEach((shape, index) => {
        console.log(`Shape ${index}:`, {
          type: shape.type,
          originalCoords: 'converted from 1920x1080',
          relativeCoords: { x: shape.x, y: shape.y, width: shape.width, height: shape.height },
          coordinateSystem: shape.coordinateSystem
        });
      });

      // Open in new tab
      window.open(finalUrl, '_blank');

    } catch (error) {
      console.error('Error opening Frame Editor:', error);
      alert(`Failed to open Frame Editor: ${error.message}`);
    }
  }, [itemId, projectId, shapeLayers, textLayers, setShowExtractFrameDropdown, currentTime]);

  // Handle color changes for text and shapes
  const handleColorChange = useCallback((color) => {
    if (selectedTextId) {
      // Update text color
      setTextLayers(layers => layers.map(layer =>
        layer.id === selectedTextId
          ? { ...layer, fill: color }
          : layer
      ));
    } else if (selectedShapeId) {
      // Update shape color (either fill or stroke depending on mode)
      setShapeLayers(layers => layers.map(layer =>
        layer.id === selectedShapeId
          ? {
              ...layer,
              fill: layer.mode === 'fill' ? color : layer.fill,
              stroke: layer.mode === 'outline' ? color : layer.stroke
            }
          : layer
      ));
    }
    setShowColorPicker(false);
  }, [selectedTextId, selectedShapeId]);

  // Handle shape copying
  const handleCopyShape = useCallback((shapeId) => {
    const selectedShape = shapeLayers.find(layer => layer.id === shapeId);
    if (selectedShape) {
      // Create a clean copy of the shape properties (excluding id)
      const { id, ...shapeProperties } = selectedShape;
      const shapeJson = JSON.stringify(shapeProperties, null, 2);
      
      // Copy to system clipboard if available
      if (navigator.clipboard) {
        navigator.clipboard.writeText(shapeJson).then(() => {
        }).catch(err => {
        });
      }
      
    }
  }, [shapeLayers]);

  // Handle shape pasting
  const handlePasteShape = useCallback((jsonString) => {
    try {
      const shapeProperties = JSON.parse(jsonString);
      
      // Validate required properties
      if (!shapeProperties.shapeType) {
        throw new Error('Missing required property: shapeType');
      }

      // Generate new unique ID
      const newId = Date.now().toString();
      
      // Create new shape with exact same properties
      const newShape = {
        ...shapeProperties,
        id: newId
      };

      // Add the new shape to the layers
      setShapeLayers(layers => [...layers, newShape]);
      
      // Select the newly pasted shape
      setSelectedShapeId(newId);
      
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Handle text deletion

  // Handle video index change
  const handleVideoIndexChange = async (newVideoIndex) => {
    try {
      console.log(`🔄 Changing video_index for clip ${itemId} from ${timelineItem?.video_index} to ${newVideoIndex}`);

      const response = await fetch(`${API_URL}/timeline-items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_index: newVideoIndex
        }),
      });

      if (response.ok) {
        console.log(`✅ Video index updated successfully to ${newVideoIndex}`);
        // Reload the page to load the correct video
        window.location.reload();
      } else {
        console.error('❌ Failed to update video index:', response.status);
        alert('Failed to update video index');
      }
    } catch (error) {
      console.error('❌ Error updating video index:', error);
      alert('Error updating video index');
    }
  };

  // Save script changes to the timeline item
  const saveScriptChanges = async () => {
    try {

      const updateData = {
        scene_description: scriptDescription
      };

      // Handle narration based on language
      if (currentLanguage === 'original') {
        updateData.scene_narration = scriptNarration;
      } else {
        // For alternate languages, update the translation
        updateData.translated_text = scriptNarration;
        updateData.language_code = currentLanguage;
      }

      const response = await fetch(`${API_URL}/timeline-items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedItem = await response.json();
        
        // Update timeline item in a targeted way to preserve existing timeline configuration
        setTimelineItem(prevItem => ({
          ...prevItem,
          scene_description: scriptDescription,
          scene_narration: currentLanguage === 'original' ? scriptNarration : prevItem.scene_narration,
          translations: currentLanguage !== 'original' 
            ? (updatedItem.translations || prevItem.translations)
            : prevItem.translations
        }));
        
        setShowScriptModal(false);
      } else {
        alert('Failed to save script changes. Please try again.');
      }
    } catch (error) {
      alert('Error saving script changes. Please try again.');
    }
  };

  // Regenerate audio for the current script
  const regenerateAudio = async () => {
    try {
      setIsGeneratingAudio(true);

      // First save the script changes if there are any
      if (scriptDescription !== (timelineItem?.scene_description || '') || 
          scriptNarration !== (currentLanguage === 'original' 
            ? (timelineItem?.scene_narration || '') 
            : (timelineItem?.translations?.find(t => t.language_code === currentLanguage)?.translated_text || ''))) {
        await saveScriptChanges();
      }

      // Generate audio using the backend endpoint
      const params = new URLSearchParams({
        voice_model: 'google',
        target_language_code: currentLanguage === 'original' ? '' : currentLanguage
      });

      const response = await fetch(`${API_URL}/timeline-items/${itemId}/generate-audio?${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update only the audio path in the timeline item without triggering configuration reset
        if (currentLanguage === 'original') {
          // For original language, update scene_audio
          setTimelineItem(prevItem => ({
            ...prevItem,
            scene_audio: result.audio_path || result.audioPath
          }));
        } else {
          // For translations, update the translations array
          setTimelineItem(prevItem => {
            const updatedTranslations = [...(prevItem.translations || [])];
            const translationIndex = updatedTranslations.findIndex(t => t.language_code === currentLanguage);
            
            if (translationIndex >= 0) {
              updatedTranslations[translationIndex] = {
                ...updatedTranslations[translationIndex],
                translated_audio_path: result.audio_path || result.audioPath
              };
            } else {
              updatedTranslations.push({
                language_code: currentLanguage,
                translated_text: scriptNarration,
                translated_audio_path: result.audio_path || result.audioPath
              });
            }
            
            return {
              ...prevItem,
              translations: updatedTranslations
            };
          });
        }
        
        // Force audio URL recalculation to pick up the new audio
        setAudioUrlKey(prev => prev + 1);
        
        // Update audio clips with new duration
        const newAudioPath = result.audio_path || result.audioPath;
        const cacheBustedPath = `${API_URL}${newAudioPath}?t=${Date.now()}`;
        
        // Create temporary audio element to get new duration
        const tempAudio = new Audio(cacheBustedPath);
        tempAudio.addEventListener('loadedmetadata', () => {
          const newDuration = tempAudio.duration;
          
          // Update audio clips with new duration and maintain current timeline position
          setAudioClips(prevClips => {
            return prevClips.map(clip => {
              if (clip.type === 'audio') {
                const currentClipDuration = clip.endTime - clip.startTime;
                return {
                  ...clip,
                  actualDuration: newDuration,
                  source: cacheBustedPath,
                  audioPath: cacheBustedPath,
                  // If the new audio is longer than current clip, we can extend it
                  // If shorter, we need to constrain it
                  endTime: Math.min(clip.endTime, clip.startTime + newDuration - (clip.audioOffset || 0))
                };
              }
              return clip;
            });
          });
        });
        tempAudio.load();
        
        
        alert('Audio generated successfully! The new audio will replace the current audio in the timeline.');
        setShowScriptModal(false);
      } else {
        alert('Failed to generate audio. Please try again.');
      }
    } catch (error) {
      alert('Error generating audio. Please try again.');
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  // Regenerate script for the current timeline item
  const regenerateScript = async () => {
    try {
      setIsRegeneratingScript(true);

      // First save the description to rawText
      const updateResponse = await fetch(`${API_URL}/timeline-items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rawText: scriptDescription
        })
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to save description');
      }

      // Now regenerate the script using the backend endpoint
      const params = new URLSearchParams();
      if (currentLanguage !== 'original') {
        params.append('target_language_code', currentLanguage);
      }

      const response = await fetch(`${API_URL}/timeline-items/${itemId}/regenerate-script${params.toString() ? `?${params}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update the narration field with the new script
        setScriptNarration(result.new_narration);
        
        // Update the timeline item
        setTimelineItem(prevItem => ({
          ...prevItem,
          rawText: scriptDescription,
          scene_narration: result.new_narration
        }));
        
        // Force audio URL recalculation in case audio needs to be refreshed
        setAudioUrlKey(prev => prev + 1);
        
        alert('Script regenerated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to regenerate script: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Error regenerating script. Please try again.');
    } finally {
      setIsRegeneratingScript(false);
    }
  };

  // Save current timeline state as clip configuration
  const saveClipConfiguration = async () => {
    try {
      
      // Determine video source for intro/outro clips
      let videoSourceUrl = project?.videoUrl || '';
      if (timelineItem && (timelineItem.type === 'intro' || timelineItem.type === 'outro')) {
        videoSourceUrl = `/media/${timelineItem.type}.mp4`;
      } else {
        // For multi-video projects, use the specific video this clip belongs to
        if (timelineItem && timelineItem.video_url) {
          // Use clip-specific video
          videoSourceUrl = timelineItem.video_url;
          console.log('💾 Saving config with clip-specific video:', videoSourceUrl);
        } else if (project?.videoUrl && typeof project.videoUrl === 'string') {
          try {
            const parsed = JSON.parse(project.videoUrl);
            if (Array.isArray(parsed) && parsed.length > 0) {
              // Use video by index or fallback to first
              const videoIndex = timelineItem && typeof timelineItem.video_index === 'number'
                ? timelineItem.video_index
                : 0;
              videoSourceUrl = parsed[videoIndex] || parsed[0];
              console.log('💾 Saving config with video at index', videoIndex, ':', videoSourceUrl);
            }
          } catch (e) {
            // Not JSON, use as-is (backward compatibility)
            console.log('💾 Saving config with single video:', videoSourceUrl);
          }
        }
      }

      const configuration = {
        timeline: {
          duration: duration,
          videoTrack: videoClips,
          audioTrack: audioClips,
          shapeLayers: shapeLayers,
          textLayers: textLayers,
        },
        projectInfo: {
          originalVideoUrl: videoSourceUrl,
          originalVideoFile: (timelineItem && (timelineItem.type === 'intro' || timelineItem.type === 'outro'))
            ? `${API_URL}/media/${timelineItem.type}.mp4`
            : (project ? `${API_URL}${project.videoUrl}` : ''),
          itemType: timelineItem?.type || 'unknown'
        },
        metadata: {
          saved_from_editor: true,
          editor_version: "1.0",
          original_language: "original",
          custom_edits: true,
          created_at: new Date().toISOString(),
          item_type: timelineItem?.type || 'unknown'
        }
      };
      
      
      const requestData = {
        language_code: currentLanguage,
        configuration: configuration
      };
      
      
      const response = await fetch(`${API_URL}/timeline-items/${itemId}/clip-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        const result = await response.json();
        setHasCustomConfig(true);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };
  
  // Load configuration after timeline item is loaded or when language changes
  useEffect(() => {
    if (timelineItem) {
      // Reset configuration loaded state when language changes
      setConfigurationLoaded(false);
      setHasCustomConfig(false);
      loadClipConfiguration();
    }
  }, [timelineItem, currentLanguage]);
  
  // Helper function to get active video clip at current timeline time
  const getActiveVideoClip = useCallback((timelineTime) => {
    const foundClip = videoClips.find(clip =>
      timelineTime >= clip.startTime && timelineTime < clip.endTime
    );

    // Check for boundary edge cases in problematic 8s-15s range
    if (timelineTime >= 8 && timelineTime <= 15) {
      if (!foundClip) {
        // Handle boundary edge case
      }
    }

    return foundClip;
  }, [videoClips]);

  // Video-to-canvas rendering system (after getActiveVideoClip is defined)
  useEffect(() => {
    let animationFrame;
    
    const drawVideoToCanvas = (timestamp) => {
      if (!canvasRef.current || !playerRef.current || !canvasReady || !canvasContainerRef.current) {
        // Log once when waiting for canvas setup
        if (!window._canvasWaitingLogged) {
          window._canvasWaitingLogged = true;
        }
        animationFrame = requestAnimationFrame(drawVideoToCanvas);
        return;
      }
      
      // Reset the waiting log flag once canvas is ready
      if (window._canvasWaitingLogged) {
        window._canvasWaitingLogged = false;
      }
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const video = playerRef.current;
      
      // Performance optimization: Only redraw when necessary
      const activeVideoClip = getActiveVideoClip(currentTime);
      const currentClipId = activeVideoClip?.id || null;
      const isFrameClip = activeVideoClip?.type === 'frame';
      
      // Check if we need to redraw
      const shouldRedraw = (
        !window._lastCanvasRender || // First render
        window._lastCanvasRender.clipId !== currentClipId || // Clip changed
        window._lastCanvasRender.forceRedraw !== forceRedraw || // Force redraw requested
        (!isFrameClip && !isScrubbingRef.current && timestamp - window._lastCanvasRender.timestamp > 33) || // Limit regular video to ~30fps (but not during scrubbing)
        (isScrubbingRef.current && activeVideoClip && video.readyState >= video.HAVE_CURRENT_DATA && timestamp - window._lastCanvasRender.timestamp > 50) // During scrubbing, only redraw when video is ready and limit to ~20fps
      );
      
      if (shouldRedraw) {
        // Only clear canvas if we're not waiting for a video seek during scrubbing
        const isWaitingForSeek = isScrubbingRef.current && activeVideoClip && video.readyState < video.HAVE_CURRENT_DATA;
        
        if (!isWaitingForSeek) {
          // Clear canvas with black background
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, 1920, 1080);
        }
        
        // Only draw video if there's an active clip and video is ready
        if (activeVideoClip && video.readyState >= video.HAVE_CURRENT_DATA) {
          const videoWidth = video.videoWidth || 1920;
          const videoHeight = video.videoHeight || 1080;
          
          // Removed canvas drawing spam log
          
          // Log first successful draw
          if (!window._firstVideoDraw) {
            window._firstVideoDraw = true;
          }
          
          // Calculate scaling to fill video within 1920x1080 canvas
          const scaleX = 1920 / videoWidth;
          const scaleY = 1080 / videoHeight;
          const videoScale = Math.max(scaleX, scaleY);
          
          // Calculate centered position
          const scaledWidth = videoWidth * videoScale;
          const scaledHeight = videoHeight * videoScale;
          const offsetX = (1920 - scaledWidth) / 2;
          const offsetY = (1080 - scaledHeight) / 2;
          
          // Draw video centered on canvas
          try {
            ctx.drawImage(video, offsetX, offsetY, scaledWidth, scaledHeight);
          } catch (error) {
            // Draw error indicator
            ctx.fillStyle = '#ff0000';
            ctx.fillRect(50, 50, 200, 100);
            ctx.fillStyle = '#ffffff';
            ctx.font = '20px Arial';
            ctx.fillText('VIDEO ERROR', 70, 110);
          }
        } else if (activeVideoClip && video.readyState < video.HAVE_CURRENT_DATA && !isScrubbingRef.current) {
          // Show loading indicator when video is not ready (but not during scrubbing)
          if (!window._videoNotReadyLogged || (Date.now() - window._videoNotReadyLogged) > 2000) {
            window._videoNotReadyLogged = Date.now();
          }
          
          // Draw loading indicator
          ctx.fillStyle = '#333333';
          ctx.fillRect(960 - 100, 540 - 25, 200, 50);
          ctx.fillStyle = '#ffffff';
          ctx.font = '16px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Loading Video...', 960, 550);
          ctx.textAlign = 'left';
        }
        
        // Update render tracking
        window._lastCanvasRender = {
          timestamp: timestamp || performance.now(),
          clipId: currentClipId,
          forceRedraw: forceRedraw
        };
      }
      
      // Continue animation loop
      animationFrame = requestAnimationFrame(drawVideoToCanvas);
    };
    
    if (canvasReady && playerRef.current) {
      drawVideoToCanvas(performance.now());
    }
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [canvasReady, playerRef.current?.src, currentTime, getActiveVideoClip, forceRedraw]);
  
  // Helper function to get active audio clip at current timeline time
  const getActiveAudioClip = useCallback((timelineTime) => {
    return audioClips.find(clip => 
      timelineTime >= clip.startTime && timelineTime < clip.endTime
    );
  }, [audioClips]);
  
  // Playback controls - define before using in other effects
  const togglePlayback = useCallback(async () => {
    
    if (isPlaying) {
      // Pause playback
      setIsPlaying(false);
      const video = playerRef.current;
      const audio = audioRef.current;
      
      // Safely pause video and audio
      try {
        if (video && !video.paused) {
          video.pause();
        }
      } catch (error) {
      }
      
      try {
        if (audio && !audio.paused) {
          audio.pause();
        }
      } catch (error) {
      }
    } else {
      // Start playback
      setIsPlaying(true);
      // The useEffect will handle starting video/audio based on currentTime
    }
  }, [isPlaying, currentTime]);
  
  // IMPERATIVE timer approach to bypass React state batching issues
  const currentTimeRef = useRef(currentTime);
  const timerIdRef = useRef(0);
  const activeTimerRef = useRef(null);

  // Refs to access current values without recreating timer
  const durationRef = useRef(duration);
  const isLoopingRef = useRef(isLooping);
  const videoClipsRef = useRef(videoClips);

  // Keep ref in sync with React state
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  // Update refs when values change
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  useEffect(() => {
    isLoopingRef.current = isLooping;
  }, [isLooping]);

  useEffect(() => {
    videoClipsRef.current = videoClips;
  }, [videoClips]);

  // IMPERATIVE timer approach - bypassing React state batching entirely
  useEffect(() => {
    // Clear any existing timer
    if (activeTimerRef.current) {
      clearInterval(activeTimerRef.current);
      activeTimerRef.current = null;
    }

    if (isPlaying) {
      timerIdRef.current += 1;
      const currentTimerId = timerIdRef.current;


      activeTimerRef.current = setInterval(() => {
        // CRITICAL: Get current time directly from ref to avoid stale closures
        const prevTime = currentTimeRef.current;
        const newTime = prevTime + 0.1;
        const currentDuration = durationRef.current;
        const currentIsLooping = isLoopingRef.current;

        // 🚨 SPECIAL DEBUG: Track 8s-15s boundary issues
        const isInProblematicRange = (prevTime >= 8 && prevTime <= 15) || (newTime >= 8 && newTime <= 15);

        if (isInProblematicRange) {

          // Check if we're jumping unexpectedly
          if (Math.abs(newTime - prevTime) > 0.15) {
          }
        } else {
        }

        // Check if we've reached the end
        if (newTime >= currentDuration) {

          if (currentIsLooping) {
            currentTimeRef.current = 0;
            originalSetCurrentTime(0);
          } else {
            // Stop playback immediately
            const video = playerRef.current;
            const audio = audioRef.current;

            // Safely pause video and audio
            try {
              if (video && !video.paused) {
                video.pause();
              }
            } catch (error) {
            }

            try {
              if (audio && !audio.paused) {
                audio.pause();
              }
            } catch (error) {
            }

            // Stop the timer and update state
            currentTimeRef.current = currentDuration;
            originalSetCurrentTime(currentDuration);
            setIsPlaying(false);
          }
        } else {
          // CRITICAL: Update both ref and React state directly
          currentTimeRef.current = newTime;
          originalSetCurrentTime(newTime);
        }
      }, 100);


    } else {
    }

    return () => {
      if (activeTimerRef.current) {
        clearInterval(activeTimerRef.current);
        activeTimerRef.current = null;
      }
    };
  }, [isPlaying, originalSetCurrentTime]); // CRITICAL: Only depend on isPlaying
  
  // Separate effect to handle video/audio sync based on currentTime
  useEffect(() => {
    // Skip if not playing AND not scrubbing
    if (!isPlaying && !isScrubbingRef.current) return;
    
    const video = playerRef.current;
    const audio = audioRef.current;
    
    // Removed noisy sync log
    
    const activeVideoClip = getActiveVideoClip(currentTime);
    const activeAudioClip = getActiveAudioClip(currentTime);
    
    // Handle video visibility and seeking
    if (video && timelineItem) {
      if (activeVideoClip) {
        // DEBUG: Enhanced logging for the 8s-15s problematic range
        if (currentTime >= 7.5 && currentTime <= 15.5) {
        }

        let actualVideoTime;
        
        if (activeVideoClip.type === 'frame') {
          // For frame clips, show the specific frame time
          actualVideoTime = activeVideoClip.frameTime;
        } else {
          // For regular video clips, calculate time based on offset
          const timelineOffsetIntoClip = currentTime - activeVideoClip.startTime;
          const videoOffsetSeconds = activeVideoClip.videoOffset || 0;
          const calculatedVideoTime = videoOffsetSeconds + timelineOffsetIntoClip;
          
          // If the calculated time exceeds the original video duration, clamp to the last frame
          const originalVideoDuration = actualVideoDuration || ((timelineItem.endTime || 0) - (timelineItem.startTime || 0));
          const videoEndTime = videoOffsetSeconds + originalVideoDuration;
          
          if (calculatedVideoTime >= videoEndTime) {
            // CRITICAL FIX: Don't interfere with timeline progression when video reaches end
            // Just show the last frame but let timeline continue advancing
            actualVideoTime = videoEndTime - 0.1; // Show last frame (slightly before end to avoid black)

            // CRITICAL: Do NOT try to control timeline progression here
            // The video sync should ONLY affect video.currentTime, never timeline currentTime
            // This was the source of the 11s-15s loop issue

          } else {
            actualVideoTime = calculatedVideoTime;
          }
        }
        
        // Show video and sync time
        video.style.opacity = '1';
        
        // Handle playback based on clip type
        if (activeVideoClip.type === 'frame') {
          // For frame clips, pause the video and show specific frame
          try {
            if (!video.paused) video.pause();
            if (Math.abs(video.currentTime - actualVideoTime) > 0.1) {
              video.currentTime = Math.max(0, actualVideoTime);
            }
          } catch (error) {
          }
        } else {
          // For regular video clips, different behavior for scrubbing vs playback
          if (isScrubbingRef.current) {
            // During scrubbing, seek immediately for responsiveness
            if (Math.abs(video.currentTime - actualVideoTime) > 0.1) {
              video.currentTime = Math.max(0, actualVideoTime);
            }
          } else {
            // During normal playback, use adaptive sync threshold
            const timeDifference = Math.abs(video.currentTime - actualVideoTime);

            // Use larger threshold right after seeking to prevent jerky corrections
            const wasRecentlyPaused = Date.now() - (video.lastPauseTime || 0) < 300; // 300ms grace period
            const syncThreshold = wasRecentlyPaused ? 0.5 : 0.15; // Larger threshold after recent pause/seek

            if (timeDifference > syncThreshold) {
              video.currentTime = Math.max(0, actualVideoTime);
            }
          }
        
        // For regular video clips, handle play/pause
        if (activeVideoClip.type !== 'frame') {
          // Play the video only if we're actually playing (not just scrubbing while paused)
          if (isPlaying && video.paused) {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
              });
            }
          }
          
          // If we're scrubbing while paused, ensure video stays paused
          if (!isPlaying && !video.paused) {
            video.pause();
          }
        }
        }
      } else {
        // Hide video (show black) - this might be causing the issue!
        if (currentTime >= 7.5 && currentTime <= 15.5) {
        } else {
        }

        video.style.opacity = '0';
        // Don't pause video here - let it continue playing for smoother scrubbing
      }
    }
    
    // Handle audio playback
    if (audio) {
      
      if (activeAudioClip) {
        const timelineOffsetIntoClip = currentTime - activeAudioClip.startTime;
        const audioOffsetSeconds = activeAudioClip.audioOffset || 0;
        const actualAudioTime = audioOffsetSeconds + timelineOffsetIntoClip;
        
        
        // Make sure audio time offset is valid (if duration is not loaded yet, allow playback)
        if (actualAudioTime >= 0 && (isNaN(audio.duration) || actualAudioTime < audio.duration)) {
          // Sync audio time only if audio is ready
          if (audio.readyState >= 1 && Math.abs(audio.currentTime - actualAudioTime) > 0.2) {
            try {
              audio.currentTime = actualAudioTime;
            } catch (error) {
            }
          }
          
          // Safely play audio only when actually playing (not when scrubbing while paused)
          if (isPlaying && audio.paused && audio.readyState >= 2) { // HAVE_CURRENT_DATA or higher
            const playPromise = audio.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                })
                .catch(error => {
                });
            }
          } else if (isPlaying && audio.paused) {
          } else if (isPlaying) {
          }
          
          // If we're scrubbing while paused, ensure audio stays paused
          if (!isPlaying && !audio.paused) {
            audio.pause();
          }
        } else {
        }
      } else {
        // No active audio clip
        try {
          if (!audio.paused) audio.pause();
        } catch (error) {
        }
      }
    } else {
    }
  }, [currentTime, isPlaying, getActiveVideoClip, getActiveAudioClip, timelineItem]);
  
  const handleClipDelete = useCallback((clipId) => {
    
    // Handle video, frame, and audio clips independently
    if (clipId.includes('video') || clipId.includes('frame')) {
      setVideoClips(clips => {
        const filteredClips = clips.filter(clip => clip.id !== clipId);
        return filteredClips;
      });
    } else if (clipId.includes('audio')) {
      setAudioClips(clips => {
        const filteredClips = clips.filter(clip => clip.id !== clipId);
        return filteredClips;
      });
    }
    
    // Clear selection if deleted clip was selected
    if (selectedClipId === clipId) {
      setSelectedClipId(null);
    }
  }, [selectedClipId]);
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlayback();
      } else if (e.key === 'l' || e.key === 'L') {
        if (e.ctrlKey) {
          // Debug key - shape state logging removed
        } else {
          setIsLooping(!isLooping);
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && (selectedClipId || selectedShapeId || selectedTextId)) {
        e.preventDefault();
        if (selectedTextId) {
          handleTextDelete(selectedTextId);
        } else if (selectedShapeId) {
          handleShapeDelete(selectedShapeId);
        } else if (selectedClipId) {
          handleClipDelete(selectedClipId);
        }
      } else if (e.key >= '0' && e.key <= '9') {
        // Number keys for seeking to specific times
        e.preventDefault();
        const timeValue = parseInt(e.key);
        if (timeValue <= duration) {
          setCurrentTime(timeValue);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlayback, isLooping, selectedClipId, selectedShapeId, selectedTextId, handleClipDelete, handleShapeDelete, handleTextDelete, shapeLayers, duration]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColorPicker && !event.target.closest('[data-color-picker]')) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColorPicker]);

  // Add throttling for seeking to prevent excessive video.currentTime updates
  const seekTimeoutRef = useRef(null);
  const isScrubbingRef = useRef(false);
  
  const handleSeek = useCallback((time) => {
    const clampedTime = Math.max(-0.1, Math.min(time, duration));
    
    // Mark as scrubbing for different playback behavior
    isScrubbingRef.current = true;
    
    // Update UI immediately for responsiveness
    setCurrentTime(clampedTime);
    
    // Clear the scrubbing flag after a delay
    setTimeout(() => {
      isScrubbingRef.current = false;
    }, 200);
    
    // Immediate seek for scrubbing responsiveness
    performActualSeek(clampedTime, true); // Pass true for isScrubbing
  }, [duration]);
  
  const performActualSeek = useCallback((clampedTime, isScrubbing = false) => {
    const video = playerRef.current;
    const audio = audioRef.current;


    // Set the scrubbing flag so the playback effect knows we're scrubbing
    if (isScrubbing) {
      isScrubbingRef.current = true;
      // Clear the scrubbing flag after a delay
      setTimeout(() => {
        isScrubbingRef.current = false;
      }, 200);
    }

    // Enhanced seeking logic to prevent jerky playback
    const wasPlaying = isPlaying;

    // If we're seeking while playing, pause first to prevent conflicts
    if (wasPlaying && !isScrubbing) {
      // Mark when we paused for seeking so sync logic can be more lenient
      if (video) {
        video.lastPauseTime = Date.now();
      }
      setIsPlaying(false);
    }

    // Update timeline position immediately for UI responsiveness
    setCurrentTime(clampedTime);

    // For backward seeks during playback, ensure video has time to buffer
    if (wasPlaying && !isScrubbing) {
      // Small delay to let video seek and buffer before resuming playback
      setTimeout(() => {
        if (video && Math.abs(video.currentTime - clampedTime) < 2.0) {
          // Only resume if the seek was successful and we're close to target time
          setIsPlaying(true);
        } else {
          // The playback effect will handle resuming when video is properly synced
          setTimeout(() => setIsPlaying(true), 100);
        }
      }, 150); // Give video time to seek and start buffering
    }
  }, [videoClips, isPlaying]);
  
  const handleTimelineClick = useCallback((e) => {
    const stage = timelineStageRef.current;
    if (stage) {
      const pos = stage.getPointerPosition();
      const contentWidth = 1200 - TRACK_LABEL_WIDTH;
      const pixelsPerSecond = duration > 0 ? contentWidth / duration : PIXELS_PER_SECOND;
      const clickTime = Math.max(0, (pos.x - TRACK_LABEL_WIDTH) / pixelsPerSecond);
      if (clickTime >= 0 && clickTime <= duration) {
        handleSeek(clickTime);
      }
    }
  }, [duration, handleSeek]);
  
  // Clip manipulation functions
  const handleClipDrag = useCallback((clipId, newStartTime) => {
    // Handle video clips separately from audio clips
    if (clipId.includes('video') || clipId.includes('frame')) {
      setVideoClips(clips => clips.map(clip => 
        clip.id === clipId 
          ? { ...clip, startTime: newStartTime, endTime: newStartTime + (clip.endTime - clip.startTime) }
          : clip
      ));
    } else if (clipId.includes('audio')) {
      setAudioClips(clips => clips.map(clip => 
        clip.id === clipId 
          ? { ...clip, startTime: newStartTime, endTime: newStartTime + (clip.endTime - clip.startTime) }
          : clip
      ));
    }
  }, []);
  
  const handleClipResize = useCallback((clipId, edge, newTime) => {
    // Handle video and audio clips independently
    const updateClip = (clip) => {
      if (clip.id === clipId) {
        if (edge === 'start') {
          if (clipId.includes('video')) {
            // For video clips: left trim should increase videoOffset and keep timeline position
            const originalStartTime = clip.startTime;
            const trimAmount = newTime - originalStartTime;
            const newVideoOffset = (clip.videoOffset || 0) + trimAmount;
            
            
            return { 
              ...clip, 
              startTime: Math.max(0, newTime),
              videoOffset: Math.max(0, newVideoOffset)
            };
          } else if (clipId.includes('frame')) {
            // For frame clips: left trim just changes timeline position (frame time stays the same)
            
            return { 
              ...clip, 
              startTime: Math.max(0, newTime)
            };
          } else {
            // For audio clips: left trim should increase audioOffset (similar to video)
            const originalStartTime = clip.startTime;
            const trimAmount = newTime - originalStartTime;
            const newAudioOffset = (clip.audioOffset || 0) + trimAmount;
            
            // Ensure we don't trim beyond the actual audio duration
            const maxAudioOffset = (clip.actualDuration || 0);
            const constrainedAudioOffset = Math.min(newAudioOffset, maxAudioOffset);
            const constrainedStartTime = originalStartTime + (constrainedAudioOffset - (clip.audioOffset || 0));
            
            
            return { 
              ...clip, 
              startTime: Math.max(0, constrainedStartTime),
              audioOffset: Math.max(0, constrainedAudioOffset)
            };
          }
        } else {
          // Right edge resize
          if (clipId.includes('audio')) {
            // For audio clips: constrain to actual audio duration
            const maxAudioTime = clip.startTime + (clip.actualDuration || 0) - (clip.audioOffset || 0);
            const constrainedEndTime = Math.min(newTime, maxAudioTime);
            return { ...clip, endTime: Math.max(clip.startTime + 0.1, constrainedEndTime) };
          } else {
            // For video clips: no audio duration constraint
            return { ...clip, endTime: Math.max(clip.startTime + 0.1, newTime) };
          }
        }
      }
      return clip;
    };
    
    if (clipId.includes('video') || clipId.includes('frame')) {
      setVideoClips(clips => clips.map(updateClip));
    } else if (clipId.includes('audio')) {
      setAudioClips(clips => clips.map(updateClip));
    }
  }, []);
  
  const handleClipSplit = useCallback((clipId, splitTime) => {
    const splitClips = (clips) => {
      const newClips = [];
      clips.forEach(clip => {
        if (clip.id === clipId && splitTime > clip.startTime && splitTime < clip.endTime) {
          // Calculate how much timeline time has passed from original start to split point
          const timeFromOriginalStart = splitTime - clip.startTime;
          
          // Create left clip (first part)
          const leftClip = {
            ...clip,
            id: `${clip.id}-a`,
            endTime: splitTime
            // videoOffset stays the same for left clip
          };
          
          // Create right clip (second part)
          const rightClip = {
            ...clip,
            id: `${clip.id}-b`,
            startTime: splitTime
          };
          
          // For video clips, adjust the videoOffset for the right clip
          if (clipId.includes('video')) {
            // The right clip should start from where the left clip ended in the video
            // So we add the timeline duration of the left clip to the original videoOffset
            rightClip.videoOffset = (clip.videoOffset || 0) + timeFromOriginalStart;
          } else if (clipId.includes('audio')) {
            // For audio clips, adjust the audioOffset for the right clip (similar to video)
            rightClip.audioOffset = (clip.audioOffset || 0) + timeFromOriginalStart;
          }
          
          newClips.push(leftClip);
          newClips.push(rightClip);
        } else {
          newClips.push(clip);
        }
      });
      return newClips;
    };
    
    // Handle video and audio clips independently
    if (clipId.includes('video') || clipId.includes('frame')) {
      setVideoClips(splitClips);
    } else if (clipId.includes('audio')) {
      setAudioClips(splitClips);
    }
  }, []);
  
  const handleAddFirstFrame = useCallback((videoClipId) => {
    
    // Find the selected video clip
    const selectedClip = videoClips.find(clip => clip.id === videoClipId);
    if (!selectedClip) return;
    
    // Calculate the actual first frame time in the video (start of current trimmed clip)
    const videoOffsetSeconds = selectedClip.videoOffset || 0;
    const timelineOffsetIntoClip = 0; // First frame is at the start of the clip
    const actualFirstFrameTime = videoOffsetSeconds + timelineOffsetIntoClip;
    
    
    // Find all clips sorted by start time
    const allVideoClips = [...videoClips].sort((a, b) => a.startTime - b.startTime);
    
    // Find the clip immediately before the selected clip
    const selectedClipIndex = allVideoClips.findIndex(clip => clip.id === videoClipId);
    const clipBefore = selectedClipIndex > 0 ? allVideoClips[selectedClipIndex - 1] : null;
    
    // Calculate available space to the left of selected clip
    const selectedClipStart = selectedClip.startTime;
    const availableStart = clipBefore ? clipBefore.endTime : 0; // Start immediately after previous clip or at timeline start
    const availableEnd = selectedClipStart; // End exactly at selected clip start
    const availableWidth = availableEnd - availableStart;
    
    // Check if there's any space available
    if (availableWidth <= 0) {
      alert(`No space available to add first frame clip. Selected clip starts at ${selectedClipStart.toFixed(2)}s with no gap before it.`);
      return;
    }
    
    // Create new frame clip that fills the available space
    const newFrameClip = {
      id: `frame-first-${Date.now()}`,
      type: 'frame',
      name: 'First Frame',
      startTime: availableStart,
      endTime: availableEnd,
      videoOffset: videoOffsetSeconds, // Same offset as original clip
      frameTime: actualFirstFrameTime, // Specific frame time in video
      originalStart: timelineItem.startTime,
      originalEnd: timelineItem.endTime,
      source: 'first-frame'
    };
    
    setVideoClips(clips => [...clips, newFrameClip]);
  }, [videoClips, duration, timelineItem]);
  
  const handleAddLastFrame = useCallback((videoClipId) => {
    
    // Find the selected video clip
    const selectedClip = videoClips.find(clip => clip.id === videoClipId);
    if (!selectedClip) return;
    
    // Calculate the actual last frame time in the video (end of current trimmed clip)
    const videoOffsetSeconds = selectedClip.videoOffset || 0;
    const clipDuration = selectedClip.endTime - selectedClip.startTime; // Duration of current trimmed clip
    const timelineOffsetIntoClip = clipDuration - 0.1; // Last frame is 0.1s before end of clip
    const actualLastFrameTime = videoOffsetSeconds + timelineOffsetIntoClip;
    
    
    // Find all clips sorted by start time
    const allVideoClips = [...videoClips].sort((a, b) => a.startTime - b.startTime);
    
    // Find the clip immediately after the selected clip
    const selectedClipIndex = allVideoClips.findIndex(clip => clip.id === videoClipId);
    const clipAfter = selectedClipIndex < allVideoClips.length - 1 ? allVideoClips[selectedClipIndex + 1] : null;
    
    // Calculate available space to the right of selected clip
    const selectedClipEnd = selectedClip.endTime;
    const availableStart = selectedClipEnd; // Start immediately after selected clip
    
    // For "fill last frame", we want to fill ALL remaining space until the end of timeline
    // This ensures we fill the gap between shortened video and the audio duration
    let availableEnd = duration; // Always extend to full timeline duration
    
    // If there are clips after the selected clip, we need to be more careful
    if (clipAfter) {
      // Check if there's a gap between selected clip and next clip
      const gapToNextClip = clipAfter.startTime - selectedClipEnd;
      if (gapToNextClip > 0) {
        // Fill only the gap to the next clip
        availableEnd = clipAfter.startTime;
      } else {
        // No gap to next clip, so extend the timeline to accommodate the last frame
        const extendedDuration = Math.max(duration, selectedClipEnd + 0.5);
        setDuration(extendedDuration);
        availableEnd = extendedDuration;
      }
    } else {
      // No clips after, fill all the way to timeline end
    }
    
    const availableWidth = availableEnd - availableStart;
    
    // Check if there's any space available
    if (availableWidth <= 0) {
      alert(`No space available to add last frame clip. Timeline may need to be extended.`);
      return;
    }
    
    // Create new frame clip that fills the available space
    const newFrameClip = {
      id: `frame-last-${Date.now()}`,
      type: 'frame',
      name: 'Last Frame',
      startTime: availableStart,
      endTime: availableEnd,
      videoOffset: actualLastFrameTime, // Offset to last frame of trimmed clip
      frameTime: actualLastFrameTime, // Specific frame time in video
      originalStart: timelineItem.startTime,
      originalEnd: timelineItem.endTime,
      source: 'last-frame'
    };
    
    setVideoClips(clips => [...clips, newFrameClip]);
  }, [videoClips, duration, timelineItem]);
  
  const handleExportTimeline = useCallback(() => {
    
    // Prepare timeline export data
    const timelineExport = {
      projectInfo: {
        projectId: parseInt(projectId),
        timelineItemId: parseInt(itemId),
        title: project?.title || 'Untitled Project',
        description: project?.description || '',
        originalVideoUrl: (timelineItem && (timelineItem.type === 'intro' || timelineItem.type === 'outro')) 
          ? `/media/${timelineItem.type}.mp4` 
          : (project?.videoUrl || ''),
        originalVideoFile: (timelineItem && (timelineItem.type === 'intro' || timelineItem.type === 'outro'))
          ? `${API_URL}/media/${timelineItem.type}.mp4`
          : (project ? `${API_URL}${project.videoUrl}` : ''),
        totalDuration: duration,
        language: currentLanguage,
        createdAt: new Date().toISOString(),
        // Suggested output filename with language, project, and timeline item info
        suggestedOutputName: `project_${projectId}_timeline_${itemId}_${currentLanguage}_${new Date().toISOString().replace(/[:.]/g, '-')}.mp4`
      },
      sourceMedia: {
        video: {
          file: (timelineItem && (timelineItem.type === 'intro' || timelineItem.type === 'outro'))
            ? `${API_URL}/media/${timelineItem.type}.mp4`
            : (project ? `${API_URL}${project.videoUrl}` : ''),
          startTime: timelineItem?.startTime || 0,
          endTime: timelineItem?.endTime || 0,
          duration: timelineItem ? (timelineItem.endTime - timelineItem.startTime) : 0
        },
        audio: timelineItem?.scene_audio ? {
          file: `${API_URL}${timelineItem.scene_audio}`,
          path: timelineItem.scene_audio,
          available: audioClips.length > 0,
          waveformGenerated: !!waveformData
        } : null
      },
      timeline: {
        duration: duration,
        videoTrack: videoClips.map(clip => {
          // Trim clips that extend beyond the timeline duration
          const clippedStartTime = Math.max(0, Math.min(clip.startTime, duration));
          const clippedEndTime = Math.max(clippedStartTime, Math.min(clip.endTime, duration));
          const clippedDuration = clippedEndTime - clippedStartTime;
          
          return {
            id: clip.id,
            type: clip.type,
            name: clip.name || `${clip.type} clip`,
            startTime: clippedStartTime,
            endTime: clippedEndTime,
            duration: clippedDuration,
            videoOffset: clip.videoOffset || 0,
            frameTime: clip.frameTime || null, // For frame clips
            source: clip.source || 'video',
            originalStart: clip.originalStart,
            originalEnd: clip.originalEnd,
            // Calculate actual video timestamps for backend
            actualVideoStartTime: clip.type === 'frame' 
              ? clip.frameTime 
              : (clip.videoOffset || 0),
            actualVideoEndTime: clip.type === 'frame' 
              ? clip.frameTime 
              : (clip.videoOffset || 0) + clippedDuration
          };
        }).filter(clip => clip.duration > 0), // Remove clips with zero duration
        audioTrack: audioClips.map(clip => {
          // Trim clips that extend beyond the timeline duration
          const clippedStartTime = Math.max(0, Math.min(clip.startTime, duration));
          const clippedEndTime = Math.max(clippedStartTime, Math.min(clip.endTime, duration));
          const clippedDuration = clippedEndTime - clippedStartTime;
          
          return {
            id: clip.id,
            type: clip.type,
            name: clip.name || 'Audio clip',
            startTime: clippedStartTime,
            endTime: clippedEndTime,
            duration: clippedDuration,
            audioOffset: clip.audioOffset || 0,
            audioPath: clip.audioPath,
            source: clip.source || 'audio',
            originalStart: clip.originalStart,
            originalEnd: clip.originalEnd,
            // Calculate actual audio timestamps for backend
            actualAudioStartTime: (clip.audioOffset || 0),
            actualAudioEndTime: (clip.audioOffset || 0) + clippedDuration
          };
        }).filter(clip => clip.duration > 0), // Remove clips with zero duration
        
        // Include shape layers for video generation
        shapeLayers: shapeLayers.map(shape => {
          // Trim shapes that extend beyond the timeline duration
          const clippedStartTime = Math.max(0, Math.min(shape.startTime, duration));
          const clippedEndTime = Math.max(clippedStartTime, Math.min(shape.endTime, duration));
          const clippedDuration = clippedEndTime - clippedStartTime;
          
          // Video generation logging removed
          
          return {
            id: shape.id,
            type: 'shape',
            shapeType: shape.shapeType || 'rectangle',
            name: shape.name || 'Shape Layer',
            startTime: clippedStartTime,
            endTime: clippedEndTime,
            duration: clippedDuration,
            // Visual properties - preserve original dimensions and scale factors
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            scaleX: shape.scaleX || 1,
            scaleY: shape.scaleY || 1,
            rotation: shape.rotation,
            // Style properties
            mode: shape.mode || 'outline',
            fill: shape.fill || 'transparent',
            stroke: shape.stroke || '#ff0000',
            strokeWidth: shape.strokeWidth || 3,
            opacity: shape.opacity || 1,
            // Fade timing
            fadeIn: shape.fadeIn || 0.5,
            fadeOut: shape.fadeOut || 0.5
          };
        }).filter(shape => shape.duration > 0),
        
        // Include text layers for video generation
        textLayers: textLayers.map(text => {
          // Trim texts that extend beyond the timeline duration
          const clippedStartTime = Math.max(0, Math.min(text.startTime, duration));
          const clippedEndTime = Math.max(clippedStartTime, Math.min(text.endTime, duration));
          const clippedDuration = clippedEndTime - clippedStartTime;
          
          return {
            id: text.id,
            type: 'text',
            name: text.name || 'Text Layer',
            text: text.text,
            startTime: clippedStartTime,
            endTime: clippedEndTime,
            duration: clippedDuration,
            // Position properties
            x: text.x,
            y: text.y,
            width: text.width * (text.scaleX || 1),
            height: text.height || text.fontSize * 1.2,
            rotation: text.rotation || 0,
            // Typography properties
            fontSize: text.fontSize,
            fontFamily: text.fontFamily,
            fontStyle: text.fontStyle,
            fill: text.fill,
            align: text.align,
            // Visual properties
            opacity: text.opacity || 1,
            padding: text.padding || 0,
            // Fade timing
            fadeIn: text.fadeInDuration || 0.5,
            fadeOut: text.fadeOutDuration || 0.5
          };
        }).filter(text => text.duration > 0),
      },
      renderSettings: {
        outputDuration: duration,
        language: currentLanguage,
        videoTracks: videoClips.length,
        audioTracks: audioClips.length,
        shapeLayers: shapeLayers.length,
        textLayers: textLayers.length,
        frameClips: videoClips.filter(clip => clip.type === 'frame').length,
        regularVideoClips: videoClips.filter(clip => clip.type === 'video').length,
        // Output naming convention using timeline item ID
        outputPath: currentLanguage === 'original' 
          ? `/generated_videos/${itemId}.mp4`
          : `/generated_videos/${currentLanguage}_${itemId}.mp4`,
        outputMetadata: {
          projectTitle: project?.title || 'Untitled Project',
          timelineItemId: itemId,
          timelineItemType: timelineItem?.type || 'unknown',
          language: currentLanguage,
          generatedAt: new Date().toISOString()
        },
        waveformData: waveformData ? {
          peaks: waveformData.peaks.length,
          duration: waveformData.duration,
          sampleRate: waveformData.sampleRate
        } : null
      },
      metadata: {
        exportedAt: new Date().toISOString(),
        exportedBy: 'ClipEditor',
        version: '1.0',
        browserInfo: navigator.userAgent,
        item_type: timelineItem?.type || 'unknown'
      }
    };
    
    
    return timelineExport;
  }, [projectId, itemId, project, timelineItem, duration, videoClips, audioClips, shapeLayers, textLayers, waveformData]);

  const handleGenerateVideo = useCallback(async () => {
    
    // Shape analysis logging removed
    
    // Reset modal state and show modal
    setVideoGenerationResult(null);
    setVideoGenerationError(null);
    setVideoGenerationProgress({ status: 'Preparing timeline data...', step: 'Initialization' });
    setIsGeneratingVideo(true);
    setIsVideoGenerationModalOpen(true);
    
    try {
      // Export timeline data
      const timelineData = handleExportTimeline();
      
      // Log important details for debugging
      
      // Log clipped clips for debugging
      const originalVideoClips = videoClips.length;
      const originalAudioClips = audioClips.length;
      const exportedVideoClips = timelineData.timeline.videoTrack.length;
      const exportedAudioClips = timelineData.timeline.audioTrack.length;
      
      if (exportedVideoClips < originalVideoClips || exportedAudioClips < originalAudioClips) {
      }
      
      // Log the actual clip data being sent
      timelineData.timeline.videoTrack.forEach((clip, index) => {
      });
      timelineData.timeline.audioTrack.forEach((clip, index) => {
      });
      timelineData.timeline.shapeLayers.forEach((shape, index) => {
      });
      timelineData.timeline.textLayers.forEach((text, index) => {
      });
      
      // Update progress
      setVideoGenerationProgress({ 
        status: 'Sending to server...', 
        step: 'Network Request',
        duration: timelineData.renderSettings.outputDuration 
      });
      
      // Send to backend for video generation
      
      // Simulate progress updates during the request
      let progressTimer;
      try {
        progressTimer = setInterval(() => {
          setVideoGenerationProgress(prev => {
            if (!prev) return null;
            
            const steps = [
              { status: 'Processing timeline data...', step: 'Timeline Processing' },
              { status: 'Rendering video clips...', step: 'Video Rendering' },
              { status: 'Applying effects and overlays...', step: 'Effects Processing' },
              { status: 'Compositing final video...', step: 'Final Composition' },
              { status: 'Encoding video file...', step: 'File Encoding' }
            ];
            
            const currentIndex = steps.findIndex(s => s.step === prev.step);
            const nextIndex = (currentIndex + 1) % steps.length;
            
            return {
              ...prev,
              ...steps[nextIndex]
            };
          });
        }, 3000); // Update every 3 seconds
        
        const response = await fetch(`${API_URL}/projects/${projectId}/generate-video-from-timeline`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(timelineData)
        });
        
        // Clear the progress timer
        clearInterval(progressTimer);
      
        if (response.ok) {
          const result = await response.json();
          
          // Set success result
          setVideoGenerationResult(result);
          setIsGeneratingVideo(false);
          setVideoGenerationProgress(null);
          
        } else {
          const error = await response.text();
          setVideoGenerationError(error);
          setIsGeneratingVideo(false);
          setVideoGenerationProgress(null);
        }
        
      } catch (innerError) {
        setVideoGenerationError(innerError.message);
        setIsGeneratingVideo(false);
        setVideoGenerationProgress(null);
        // Clear timer on error
        if (progressTimer) {
          clearInterval(progressTimer);
        }
      }
      
    } catch (error) {
      setVideoGenerationError(error.message);
      setIsGeneratingVideo(false);
      setVideoGenerationProgress(null);
    }
  }, [projectId, handleExportTimeline, duration, shapeLayers, textLayers, videoClips, audioClips, currentLanguage, itemId]);
  
  const handleDownloadTimelineJson = useCallback(() => {
    const timelineData = handleExportTimeline();
    
    // Download as JSON file
    const jsonString = JSON.stringify(timelineData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `timeline_export_${projectId}_${itemId}_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
  }, [handleExportTimeline, projectId, itemId]);

  const handleBackToEditor = () => {
    navigate(`/project/${projectId}`);
  };

  const handleCloseVideoGenerationModal = () => {
    setIsVideoGenerationModalOpen(false);
    // Reset all modal state
    setIsGeneratingVideo(false);
    setVideoGenerationProgress(null);
    setVideoGenerationResult(null);
    setVideoGenerationError(null);
  };

  if (isLoading) {
    return (
      <div className="clip-editor-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading clip editor...</p>
        </div>
      </div>
    );
  }

  if (!timelineItem || !project) {
    return (
      <div className="clip-editor-page">
        <div className="loading-container">
          <p>Clip not found</p>
          <button onClick={handleBackToEditor} className="back-button">
            Back to Editor
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="clip-editor-page">
      {/* Header */}
      <header className="clip-editor-header">
        <h1 className="clip-editor-title">
          <div className="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.5 3.5c-.614-.884-.074-1.962.858-2.5L8 7.226 11.642 1c.932.538 1.472 1.616.858 2.5L8.81 8.61l1.556 2.661c.372.637.068 1.434-.6 1.756a1.102 1.102 0 0 1-1.444-.392L8.125 12.4 8 12.027 7.875 12.4l-.197.235a1.102 1.102 0 0 1-1.444.392c-.668-.322-.972-1.119-.6-1.756L7.19 8.61 3.5 3.5zm2.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm7 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/>
            </svg>
          </div>
          {itemIndex ? `Clip ${itemIndex}` : 'Clip Editor'}
          <span style={{ fontSize: '0.6em', color: '#6c757d' }}>
            {timelineItem.type === 'clip' 
              ? `${timelineItem.startTime?.toFixed(2)}s - ${timelineItem.endTime?.toFixed(2)}s`
              : 'Timeline Item'
            }
            {currentLanguage !== 'original' && (
              <span style={{ marginLeft: '10px', color: '#007bff' }}>
                • {currentLanguage.toUpperCase()}
                {hasCustomConfig && <span style={{ color: '#28a745' }}> (Custom)</span>}
              </span>
            )}
          </span>
        </h1>

        {/* Video Index Selector */}
        {project && project.videoUrl && (() => {
          try {
            const parsed = JSON.parse(project.videoUrl);
            if (Array.isArray(parsed) && parsed.length > 1) {
              return (
                <div className="video-index-selector" style={{ margin: '10px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label htmlFor="video-index-select" style={{ fontSize: '14px', color: '#6c757d' }}>
                    Video Source:
                  </label>
                  <select
                    id="video-index-select"
                    value={timelineItem?.video_index || 0}
                    onChange={(e) => handleVideoIndexChange(parseInt(e.target.value))}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    {parsed.map((videoUrl, index) => (
                      <option key={index} value={index}>
                        Video {index + 1} ({videoUrl.split('/').pop() || `video-${index + 1}`})
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
          } catch (e) {
            // Not a JSON array, single video
          }
          return null;
        })()}

        <div className="header-actions">
          <button 
            onClick={async () => {
              const success = await saveClipConfiguration();
              if (success) {
                alert('Clip configuration saved successfully!');
              } else {
                alert('Failed to save clip configuration');
              }
            }}
            className="export-button"
            title={`Save Custom Configuration for ${currentLanguage}`}
            style={{ marginRight: '10px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 3H5c-1.11 0-2 .89-2 2v14c0 1.11.89 2 2 2h14c1.11 0 2-.89 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
            </svg>
            Save
          </button>
          
          <button 
            onClick={showResetConfirmation} 
            className="reset-button"
            title="Reset timeline configuration"
            style={{ marginRight: '10px' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
            </svg>
            Reset
          </button>
          
          {/* Hide Generate Clip button on production */}
          {API_URL !== 'https://api-guru.e-so.in' && (
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleGenerateVideo();
              }}
              className="generate-button"
              title="Generate Clip from Timeline"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
              Generate Clip
            </button>
          )}
          
          <button 
            onClick={() => navigate(`/project/${projectId}/quick-preview`)} 
            className="back-button" 
            style={{ marginRight: '10px' }}
            title="Go to Quick Preview"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Quick Preview
          </button>
          
          <button onClick={handleBackToEditor} className="back-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Back to Editor
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="clip-editor-main">
        {/* Video Canvas */}
        <div className="video-canvas-container">
          <div 
            ref={canvasContainerRef}
            className="video-preview"
            onClick={(e) => {
              // Deselect clips/shapes when clicking on video canvas area
              // Note: Konva elements will handle their own clicks, so this only triggers on video/empty areas
              const isCanvasElement = e.target.tagName === 'CANVAS';
              if (!isCanvasElement) {
                setSelectedClipId(null);
                setSelectedShapeId(null);
                setSelectedTextId(null);
              }
            }}
          >
            {!playerReady && (
              <div className="video-loading">
                <div className="loading-spinner"></div>
                <p>Preloading video...</p>
                <small>Waiting for video to be fully buffered for smooth playback</small>
                <small>URL: {project?.videoUrl}</small>
                <small>Full URL: {project ? `${API_URL}${encodeURI(project.videoUrl)}` : 'No project'}</small>
              </div>
            )}
            
            {/* Fixed 1920x1080 Canvas */}
            <canvas
              ref={canvasRef}
              width={1920}
              height={1080}
              style={{
                width: canvasScale > 0 ? `${1920 * canvasScale}px` : '800px',
                height: canvasScale > 0 ? `${1080 * canvasScale}px` : '450px',
                backgroundColor: '#000',
                display: 'block'
              }}
            />
            
            {/* Hidden video element for canvas rendering */}
            {project && (
              <video
                ref={playerRef}
                src={`${API_URL}${encodeURI(project.videoUrl)}`}
                style={{ 
                  display: 'none' // Hidden - only used for canvas rendering
                }}
                crossOrigin="anonymous"
                preload="auto"
                loop={false} // Force disable video element loop to prevent conflicts with timeline
                onLoadStart={() => {
                  setPlayerReady(false);
                }}
                onLoadedMetadata={() => {
                  const video = playerRef.current;
                  if (video && timelineItem) {

                    // CRITICAL FIX: Only reset timeline on initial load, not during playback
                    if (!isPlaying && currentTime === 0) {
                      const clipStart = timelineItem.startTime || 0;
                      video.currentTime = Math.max(0, clipStart);
                      setCurrentTime(0);
                    }
                    
                    // Initially hide video if no active clip at time 0
                    const activeVideoClip = getActiveVideoClip(0);
                    video.style.opacity = activeVideoClip ? '1' : '0';
                  }
                }}
                onLoadedData={() => {
                  // Force canvas redraw when first frame is available
                  setForceRedraw(prev => prev + 1);
                  
                  // Ensure video element is properly set up for canvas drawing
                  const video = playerRef.current;
                  if (video && video.readyState >= video.HAVE_CURRENT_DATA) {
                    // Video data is ready for canvas rendering
                    setPlayerReady(true);
                  }
                }}
                onCanPlay={() => {
                  // Force a canvas redraw when video becomes playable
                  const video = playerRef.current;
                  if (video && canvasRef.current) {
                    setForceRedraw(prev => prev + 1);
                  }
                }}
                onCanPlayThrough={() => {
                  setPlayerReady(true);
                  // Force canvas redraw
                  const video = playerRef.current;
                  if (video && canvasRef.current) {
                    setForceRedraw(prev => prev + 1);
                  }
                }}
                onWaiting={() => {
                }}
                onTimeUpdate={() => {
                  // Timeline-based playback handled by separate timer
                }}
                onPlay={() => {
                  // Don't update isPlaying here - let timeline control it
                }}
                onPause={() => {
                  // Don't update isPlaying here - let timeline control it
                }}
                onError={(error) => {
                  setPlayerReady(false); // Reset ready state on error
                  
                  // Check if it's a CORS-related error
                  if (error.target && error.target.error) {
                  }
                  
                  // Show user-friendly error message
                  alert('Failed to load video. This might be a CORS (cross-origin) issue if the video is served remotely.');
                }}
                controls={false}
              >
                Your browser does not support the video tag.
              </video>
            )}
            

            {/* Canvas overlay layer - Konva Stage for text and shapes */}
            {playerReady && canvasReady && (textLayers.length > 0 || shapeLayers.length > 0) && (
              <CanvasOverlay
                textLayers={textLayers}
                setTextLayers={setTextLayers}
                selectedTextId={selectedTextId}
                setSelectedTextId={setSelectedTextId}
                editingTextId={editingTextId}
                setEditingTextId={setEditingTextId}
                shapeLayers={shapeLayers}
                setShapeLayers={setShapeLayers}
                selectedShapeId={selectedShapeId}
                setSelectedShapeId={setSelectedShapeId}
                currentTime={currentTime}
                canvasScale={canvasScale}
              />
            )}

            {/* HTML-based text editor overlay */}
            {editingTextId && (() => {
              const editingText = textLayers.find(t => t.id === editingTextId);
              if (!editingText) return null;
              
              const containerRect = canvasContainerRef.current?.getBoundingClientRect();
              if (!containerRect) return null;
              
              // Calculate position based on text position and canvas scale
              const textX = editingText.x * canvasScale;
              const textY = editingText.y * canvasScale;
              const textWidth = editingText.width * canvasScale;
              const fontSize = editingText.fontSize * canvasScale;
              
              // Center the canvas within the container
              const canvasOffsetX = (containerRect.width - (1920 * canvasScale)) / 2;
              const canvasOffsetY = (containerRect.height - (1080 * canvasScale)) / 2;
              
              const editorStyle = {
                position: 'absolute',
                left: `${canvasOffsetX + textX}px`,
                top: `${canvasOffsetY + textY}px`,
                width: `${textWidth}px`,
                fontSize: `${fontSize}px`,
                fontFamily: editingText.fontFamily,
                fontWeight: editingText.fontStyle === 'bold' ? 'bold' : 'normal',
                color: editingText.fill,
                backgroundColor: 'transparent',
                border: '1px dashed rgba(255, 255, 255, 0.5)',
                outline: 'none',
                resize: 'none',
                overflow: 'hidden',
                textAlign: 'left',
                padding: '2px',
                zIndex: 1000,
                minHeight: `${fontSize + 4}px`
              };
              
              return (
                <textarea
                  autoFocus
                  defaultValue={editingText.text}
                  style={editorStyle}
                  onChange={(e) => {
                    // Update text and width in real-time as user types
                    handleTextChange(editingTextId, e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      setEditingTextId(null);
                    }
                    if (e.key === 'Escape') {
                      setEditingTextId(null);
                    }
                  }}
                  onBlur={(e) => {
                    setEditingTextId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              );
            })()}
            
            {/* Audio element for audio playback */}
            {audioClips.length > 0 && audioClips[0].audioPath && (
              <audio
                ref={audioRef}
                src={`${API_URL}${audioClips[0].audioPath}`}
                preload="metadata"
                crossOrigin="anonymous"
                onCanPlay={() => {
                  const audio = audioRef.current;
                  if (audio) {
                    // Audio can start playing
                  }
                }}
                onLoadedMetadata={() => {
                  const audio = audioRef.current;
                  if (audio) {
                    // Audio metadata loaded
                    setActualAudioDuration(audio.duration);
                  }
                }}
                onError={(error) => {
                  console.error('Audio error:', error);
                  const audio = audioRef.current;
                  if (audio) {
                    console.error('Audio state:', {
                      error: audio.error,
                      networkState: audio.networkState,
                      readyState: audio.readyState
                    });
                  }
                }}
                onLoadStart={() => {
                  // Audio loading started
                }}
                onPlay={() => {
                  // Audio playback started
                }}
                onPause={() => {
                  // Audio playback paused
                }}
                style={{ display: 'none' }}
              >
                Your browser does not support the audio element.
              </audio>
            )}
          </div>
        </div>

        {/* Playback Controls */}
        <div className="playback-controls">
          <button 
            className={`control-button ${isPlaying ? 'playing' : ''}`}
            onClick={togglePlayback}
            disabled={!playerReady}
            title={!playerReady ? 'Video loading...' : (isPlaying ? 'Pause' : 'Play')}
          >
            <div className="button-icon">
              {isPlaying ? (
                // Pause icon - two vertical bars
                <div className="pause-icon">
                  <div className="pause-bar"></div>
                  <div className="pause-bar"></div>
                </div>
              ) : (
                // Play icon - triangle
                <div className="play-icon">
                  <div className="play-triangle"></div>
                </div>
              )}
            </div>
          </button>
          
          <button 
            className={`control-button ${isLooping ? 'active' : ''}`}
            onClick={() => setIsLooping(!isLooping)}
            title={isLooping ? 'Loop: On' : 'Loop: Off'}
          >
            <div className="button-icon">
              <div className="loop-icon">
                <div className="loop-circle"></div>
                <div className="loop-arrow"></div>
              </div>
            </div>
          </button>
          
          <button 
            className="control-button"
            onClick={() => {
              if (selectedClipId) {
                handleClipSplit(selectedClipId, currentTime);
              }
            }}
            disabled={!selectedClipId}
            title="Split Selected Clip"
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9.64 7.64c.23-.5.36-1.05.36-1.64 0-2.21-1.79-4-4-4S2 3.79 2 6s1.79 4 4 4c.59 0 1.14-.13 1.64-.36L10 12l-2.36 2.36C7.14 14.13 6.59 14 6 14c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4c0-.59-.13-1.14-.36-1.64L12 14l7 7h3v-1L9.64 7.64zM6 8c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm0 12c-1.1 0-2-.89-2-2s.9-2 2-2 2 .89 2 2-.9 2-2 2zm6-7.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5.5.22.5.5-.22.5-.5.5zM19 3l-6 6 2 2 7-7V3z"/>
              </svg>
            </div>
          </button>
          
          <button 
            className="control-button"
            onClick={() => {
              if (selectedClipId && selectedClipId.includes('video')) {
                handleAddFirstFrame(selectedClipId);
              }
            }}
            disabled={!selectedClipId || !selectedClipId.includes('video')}
            title="Add First Frame of Selected Video Clip"
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"/>
                <rect x="1" y="1" width="2" height="22" fill="currentColor"/>
              </svg>
            </div>
          </button>
          
          <button 
            className="control-button"
            onClick={() => {
              if (selectedClipId && selectedClipId.includes('video')) {
                handleAddLastFrame(selectedClipId);
              }
            }}
            disabled={!selectedClipId || !selectedClipId.includes('video')}
            title="Add Last Frame of Selected Video Clip"
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"/>
                <rect x="21" y="1" width="2" height="22" fill="currentColor"/>
              </svg>
            </div>
          </button>
          
          <button 
            className="control-button"
            onClick={() => {
              if (selectedTextId) {
                handleTextDelete(selectedTextId);
              } else if (selectedShapeId) {
                handleShapeDelete(selectedShapeId);
              } else if (selectedClipId) {
                handleClipDelete(selectedClipId);
              }
            }}
            disabled={!selectedClipId && !selectedShapeId && !selectedTextId}
            style={{ color: (selectedClipId || selectedShapeId || selectedTextId) ? '#f44336' : undefined }}
            title={selectedTextId ? "Delete Selected Text" : selectedShapeId ? "Delete Selected Shape" : "Delete Selected Clip"}
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </div>
          </button>
          
          {/* Copy Button - Only show when shape layer is selected */}
          <button 
            className="control-button"
            onClick={() => {
              if (selectedShapeId) {
                handleCopyShape(selectedShapeId);
              }
            }}
            disabled={!selectedShapeId}
            style={{ color: selectedShapeId ? '#4CAF50' : undefined }}
            title="Copy Selected Shape"
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
              </svg>
            </div>
          </button>

          {/* Paste Button */}
          <button 
            className="control-button"
            onClick={() => setShowPasteModal(true)}
            title="Paste Shape Layer"
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z"/>
              </svg>
            </div>
          </button>
          
          {/* Shape Menu with Dropdown */}
          <div className="shape-selector-container" style={{ position: 'relative', display: 'inline-block' }}>
            <button 
              className="control-button"
              onClick={() => setShowShapeDropdown(!showShapeDropdown)}
              title="Add Shape Layer"
            >
              <div className="button-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h18v18H3V3zm2 2v14h14V5H5z"/>
                </svg>
              </div>
            </button>
            
            {showShapeDropdown && (
              <div className="dropdown-menu" style={{
                position: 'absolute',
                bottom: '35px',
                left: '0',
                background: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '4px',
                padding: '4px 0',
                minWidth: '140px',
                zIndex: 1000,
                boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
              }}>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    handleAddShape('rectangle');
                    setShowShapeDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#3a3a3a'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 3h18v18H3V3zm2 2v14h14V5H5z"/>
                  </svg>
                  Rectangle
                </button>
                
                <button
                  className="dropdown-item"
                  onClick={() => {
                    handleAddShape('circle');
                    setShowShapeDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#3a3a3a'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="9"/>
                  </svg>
                  Circle
                </button>
                
                <button
                  className="dropdown-item"
                  onClick={() => {
                    handleAddShape('arrow');
                    setShowShapeDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#3a3a3a'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                  </svg>
                  Arrow
                </button>
              </div>
            )}
          </div>


          
          {/* Shape Mode Selector */}
          <div className="mode-selector-container" style={{ position: 'relative' }}>
            <button 
              className="control-button"
              onClick={() => setShowModeDropdown(!showModeDropdown)}
              title={`Shape Mode: ${shapeMode.charAt(0).toUpperCase() + shapeMode.slice(1)}`}
            >
              <div className="button-icon">
                {shapeMode === 'outline' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  </svg>
                )}
                {shapeMode === 'fill' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                  </svg>
                )}
                {shapeMode === 'blur' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 2c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>
                  </svg>
                )}
                <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '2px' }}>
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </div>
            </button>
            
            {/* Dropdown Menu */}
            {showModeDropdown && (
              <div className="mode-dropdown" style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                backgroundColor: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '4px',
                zIndex: 1000,
                minWidth: '120px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                <button 
                  className={`mode-option ${shapeMode === 'outline' ? 'active' : ''}`}
                  onClick={() => {
                    setShapeMode('outline');
                    setShowModeDropdown(false);
                    // Update selected shape if any
                    if (selectedShapeId) {
                      setShapeLayers(layers => layers.map(layer => 
                        layer.id === selectedShapeId 
                          ? { 
                              ...layer, 
                              mode: 'outline',
                              fill: 'transparent',
                              stroke: '#ff0000',
                              strokeWidth: 3,
                              // Restore fade properties when switching from blur to outline
                              fadeIn: 0.5,
                              fadeOut: 0.5,
                              fadeInDuration: 0.5,
                              fadeOutDuration: 0.5,
                              holdDuration: Math.max(0, (layer.endTime - layer.startTime) - 1.0)
                            }
                          : layer
                      ));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: shapeMode === 'outline' ? '#007bff' : 'transparent',
                    color: '#fff',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  </svg>
                  Outline
                </button>
                <button 
                  className={`mode-option ${shapeMode === 'fill' ? 'active' : ''}`}
                  onClick={() => {
                    setShapeMode('fill');
                    setShowModeDropdown(false);
                    // Update selected shape if any
                    if (selectedShapeId) {
                      setShapeLayers(layers => layers.map(layer => 
                        layer.id === selectedShapeId 
                          ? { 
                              ...layer, 
                              mode: 'fill',
                              fill: '#ff0000',
                              stroke: 'transparent',
                              strokeWidth: 0,
                              // Restore fade properties when switching from blur to fill
                              fadeIn: 0.5,
                              fadeOut: 0.5,
                              fadeInDuration: 0.5,
                              fadeOutDuration: 0.5,
                              holdDuration: Math.max(0, (layer.endTime - layer.startTime) - 1.0)
                            }
                          : layer
                      ));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: shapeMode === 'fill' ? '#007bff' : 'transparent',
                    color: '#fff',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                  </svg>
                  Fill
                </button>
                <button 
                  className={`mode-option ${shapeMode === 'blur' ? 'active' : ''}`}
                  disabled={(() => {
                    // Disable blur mode for arrows
                    if (selectedShapeId) {
                      const selectedShape = shapeLayers.find(layer => layer.id === selectedShapeId);
                      return selectedShape && selectedShape.shapeType === 'arrow';
                    }
                    return false;
                  })()}
                  onClick={() => {
                    // Check if selected shape is arrow - don't allow blur for arrows
                    if (selectedShapeId) {
                      const selectedShape = shapeLayers.find(layer => layer.id === selectedShapeId);
                      if (selectedShape && selectedShape.shapeType === 'arrow') {
                        return; // Don't allow blur for arrows
                      }
                    }
                    
                    setShapeMode('blur');
                    setShowModeDropdown(false);
                    // Update selected shape if any (only for rectangle and circle)
                    if (selectedShapeId) {
                      setShapeLayers(layers => layers.map(layer => 
                        layer.id === selectedShapeId 
                          ? { 
                              ...layer, 
                              mode: 'blur',
                              fill: 'transparent',
                              stroke: 'transparent',
                              strokeWidth: 0,
                              // Remove fade properties for blur mode
                              fadeIn: undefined,
                              fadeOut: undefined,
                              fadeInDuration: undefined,
                              fadeOutDuration: undefined,
                              holdDuration: undefined,
                            }
                          : layer
                      ));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: shapeMode === 'blur' ? '#007bff' : 'transparent',
                    color: (() => {
                      // Check if button is disabled (arrow selected)
                      if (selectedShapeId) {
                        const selectedShape = shapeLayers.find(layer => layer.id === selectedShapeId);
                        if (selectedShape && selectedShape.shapeType === 'arrow') {
                          return '#666'; // Dimmed color for disabled state
                        }
                      }
                      return '#fff';
                    })(),
                    textAlign: 'left',
                    cursor: (() => {
                      // Check if button is disabled (arrow selected)
                      if (selectedShapeId) {
                        const selectedShape = shapeLayers.find(layer => layer.id === selectedShapeId);
                        if (selectedShape && selectedShape.shapeType === 'arrow') {
                          return 'not-allowed'; // Show not-allowed cursor for disabled state
                        }
                      }
                      return 'pointer';
                    })(),
                    fontSize: '12px',
                    opacity: (() => {
                      // Check if button is disabled (arrow selected)
                      if (selectedShapeId) {
                        const selectedShape = shapeLayers.find(layer => layer.id === selectedShapeId);
                        if (selectedShape && selectedShape.shapeType === 'arrow') {
                          return 0.5; // Dimmed opacity for disabled state
                        }
                      }
                      return 1;
                    })()
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px' }}>
                    <path d="M6 2c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>
                  </svg>
                  Blur
                </button>
              </div>
            )}
          </div>
          
          {/* Text Tools - positioned adjacent to shape tools */}
          <button 
            className="control-button"
            onClick={handleAddText}
            title="Add Text Layer"
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 4v3h5.5v12h3V7H19V4z"/>
              </svg>
            </div>
          </button>

          {/* Font Size Selector - Only show when text layer is selected */}
          {selectedTextId && (
            <div className="font-size-selector-container" style={{ position: 'relative', display: 'inline-block' }}>
              <button 
                className="control-button"
                onClick={() => setShowFontSizeDropdown(!showFontSizeDropdown)}
                title={`Font Size: ${textLayers.find(t => t.id === selectedTextId)?.fontSize || 48}px`}
                style={{ minWidth: '45px', fontSize: '11px' }}
              >
                {textLayers.find(t => t.id === selectedTextId)?.fontSize || 48}px
              </button>
              
              {showFontSizeDropdown && (
                <div className="dropdown-menu" style={{
                  position: 'absolute',
                  bottom: '35px',
                  left: '0',
                  background: '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '4px',
                  minWidth: '120px',
                  zIndex: 1000,
                  boxShadow: '0 -2px 8px rgba(0,0,0,0.3)'
                }}>
                  {/* Common font sizes */}
                  {[12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72, 84, 96].map(size => (
                    <div
                      key={size}
                      className="dropdown-item"
                      onClick={() => {
                        handleFontSizeChange(selectedTextId, size);
                        setShowFontSizeDropdown(false);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #444',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#3a3a3a'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      {size}px
                    </div>
                  ))}
                  
                  {/* Custom size input */}
                  <div style={{ padding: '8px 12px', borderTop: '1px solid #555' }}>
                    <input
                      type="number"
                      placeholder="Custom size"
                      value={customFontSize}
                      onChange={(e) => setCustomFontSize(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && customFontSize && parseInt(customFontSize) > 0) {
                          handleFontSizeChange(selectedTextId, parseInt(customFontSize));
                          setCustomFontSize('');
                          setShowFontSizeDropdown(false);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '4px',
                        background: '#1a1a1a',
                        border: '1px solid #555',
                        borderRadius: '2px',
                        color: '#fff',
                        fontSize: '11px'
                      }}
                    />
                    <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>
                      Press Enter to apply
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Script Editing Button */}
          <button 
            className="control-button"
            onClick={() => {
              // Ensure script fields are populated when modal opens
              if (timelineItem) {
                if (currentLanguage === 'original') {
                  // Use scene_description if available, otherwise fall back to rawText
                  const description = timelineItem.scene_description || timelineItem.rawText || '';
                  setScriptDescription(description);
                  setScriptNarration(timelineItem.scene_narration || '');
                } else {
                  const translation = timelineItem.translations?.find(t => t.language_code === currentLanguage);
                  // Use scene_description if available, otherwise fall back to rawText
                  const description = timelineItem.scene_description || timelineItem.rawText || '';
                  setScriptDescription(description);
                  setScriptNarration(translation?.translated_text || timelineItem.scene_narration || '');
                }
              }
              setShowScriptModal(true);
            }}
            title="Edit Script"
            style={{ marginLeft: '5px' }}
          >
            <div className="button-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#fff' }}>
                <path d="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"/>
              </svg>
            </div>
          </button>

          {/* Color Picker - only show when text or shape is selected */}
          {(selectedTextId || selectedShapeId) && (
            <div style={{ position: 'relative', display: 'inline-block', marginLeft: '5px' }} data-color-picker>
              <button
                className="control-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowColorPicker(!showColorPicker);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                title="Change Color"
              >
                <div className="button-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18V6Z"/>
                  </svg>
                </div>
              </button>

              {/* Color Picker Dropdown */}
              {showColorPicker && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  background: '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  padding: '10px',
                  zIndex: 1000,
                  minWidth: '180px'
                }}>
                  <div style={{ marginBottom: '8px', fontSize: '12px', color: '#ccc' }}>
                    Color Swatches
                  </div>

                  {/* Basic Color Swatches */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: '4px',
                    marginBottom: '10px'
                  }}>
                    {['#ffffff', '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff',
                      '#00ffff', '#ffa500', '#800080', '#008000', '#ffc0cb', '#000000'].map(color => (
                      <button
                        key={color}
                        style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: color,
                          border: '1px solid #666',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleColorChange(color);
                        }}
                        title={color}
                      />
                    ))}
                  </div>

                  {/* Custom Color Input */}
                  <div style={{ marginBottom: '8px', fontSize: '12px', color: '#ccc' }}>
                    Custom Color
                  </div>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      style={{
                        width: '30px',
                        height: '30px',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleColorChange(customColor);
                      }}
                      style={{
                        padding: '4px 8px',
                        background: '#444',
                        color: '#fff',
                        border: '1px solid #666',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="time-display-container">
            {/* Extract Frame Menu */}
            <div className="extract-frame-container" style={{ position: 'relative', marginRight: '10px' }}>
              <button
                className="extract-frame-button"
                onClick={() => setShowExtractFrameDropdown(!showExtractFrameDropdown)}
                title="Extract Frame"
                style={{
                  background: '#444',
                  border: '1px solid #666',
                  borderRadius: '4px',
                  color: '#fff',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#555'}
                onMouseLeave={(e) => e.target.style.background = '#444'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  <path d="M9 16l-5-5 1.41-1.41L9 13.17l7.59-7.59L18 7l-9 9z"/>
                </svg>
                Extract
              </button>

              {/* Extract Frame Dropdown */}
              {showExtractFrameDropdown && (
                <div className="extract-frame-dropdown" style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  zIndex: 10000,
                  background: '#2a2a2a',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  padding: '8px 0',
                  minWidth: '180px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  marginTop: '4px'
                }}>
                  <div
                    className="extract-frame-option"
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      color: '#fff',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'transparent'
                    }}
                    onClick={handleCaptureFrame}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H9l4-4 4 4z"/>
                    </svg>
                    Capture Frame
                  </div>

                  <div
                    className="extract-frame-option"
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      color: '#fff',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'transparent'
                    }}
                    onClick={handleOpenFrameEditor}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                    Open Frame Editor
                  </div>
                </div>
              )}
            </div>

            <div className="zoom-controls">
              <button 
                onClick={() => setCanvasScale(Math.min(canvasScale * 1.2, 3))}
                className="zoom-button"
                title="Zoom In"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
              </button>
              <button 
                onClick={() => setCanvasScale(Math.max(canvasScale * 0.8, 0.1))}
                className="zoom-button"
                title="Zoom Out"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13H5v-2h14v2z"/>
                </svg>
              </button>
            </div>
            <div className="duration-control-inline">
              <input
                type="number"
                min="0.1"
                max="600"
                step="0.1"
                value={customDuration}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setCustomDuration(newValue);
                  // Real-time update
                  const parsedDuration = parseFloat(newValue);
                  if (parsedDuration > 0) {
                    handleDurationChange(parsedDuration);
                  }
                }}
                className="duration-input-inline"
                title="Timeline duration in seconds"
              />
              <span className="duration-unit-inline">s</span>
              <button 
                onClick={resetDurationToOriginal}
                className="duration-reset-inline"
                title="Reset to original duration"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                </svg>
              </button>
            </div>
            
          </div>
        </div>

        {/* Timeline */}
        <div className="timeline-container">
          {/* Fixed ruler and playhead layer */}
          <div className="timeline-fixed-layer">
            <Stage
              width={1200}
              height={40}
            >
              <Layer>
                {/* Time ruler */}
                <TimeRuler 
                  duration={duration} 
                  availableWidth={1200} 
                  onSeek={handleSeek}
                  interactive={true}
                />
                {/* Fixed playhead */}
                <Playhead currentTime={currentTime} duration={duration} availableWidth={1200} height={40} />
              </Layer>
            </Stage>
          </div>
          
          {/* Scrollable content */}
          <div className="timeline-scrollable">
            <div className="timeline-content">
              <Stage
                key={timelineKey} // Force re-render when timeline resets
                width={1200} // Fixed large width to use available space
                height={Math.max(180, ((textLayers.length + shapeLayers.length) * 70) + 140)} // Dynamic height based on text and shape tracks
                ref={timelineStageRef}
                onClick={(e) => {
                  // Don't deselect if currently dragging
                  if (isDragging) return;
                  
                  // Only deselect if clicking on empty space (not on clips, resize handles, etc.)
                  const clickedOn = e.target;
                  const stage = e.target.getStage();
                  const isStageBackground = clickedOn === stage;
                  const isTrackBackground = clickedOn.className === 'Rect' && 
                    (clickedOn.attrs.fill === '#333' || // Track label area
                     clickedOn.attrs.fill === '#2a2a2a' || // Video track background  
                     clickedOn.attrs.fill === '#1a1a1a'); // Audio/shapes track background
                  
                  // Don't deselect if clicking on a clip (let the clip handle its own selection)
                  const isClipElement = clickedOn.className === 'Rect' && 
                    (clickedOn.attrs.fill === '#4a90e2' || // Video clip
                     clickedOn.attrs.fill === '#9b59b6' || // Frame clip
                     clickedOn.attrs.fill === '#b85450' || // Shape clip  
                     clickedOn.attrs.fill === '#e67e22' || // Text clip
                     clickedOn.attrs.fill === '#1a3a1a'); // Audio clip
                  
                  if ((isStageBackground || isTrackBackground) && !isClipElement) {
                    setSelectedClipId(null);
                    setSelectedShapeId(null);
                    setSelectedTextId(null);
                  }
                }}
              >
                <Layer>
                  {/* Text tracks - always above other tracks, each text gets its own track */}
                  {textLayers.map((text, index) => (
                    <TimelineTrack
                      key={text.id}
                      trackType="text"
                      clips={[text]} // Each text gets its own track
                      duration={duration}
                      availableWidth={1200}
                      onClipDrag={handleTextDrag}
                      onClipResize={handleTextResize}
                      onClipSplit={() => {}} // Text doesn't split
                      selectedClipId={selectedTextId}
                      onClipSelect={(textId) => {
                        // Always select, never deselect when clicking on text
                        setSelectedTextId(textId);
                        // Also clear other selections
                        setSelectedClipId(null);
                        setSelectedShapeId(null);
                      }}
                      onClipRightClick={handleLayerRightClick}
                      trackIndex={index} // Pass index for positioning
                    />
                  ))}

                  {/* Shapes tracks - only show if there are shapes, each shape gets its own track */}
                  {shapeLayers.map((shape, index) => (
                    <TimelineTrack
                      key={shape.id}
                      trackType="shapes"
                      clips={[shape]} // Each shape gets its own track
                      duration={duration}
                      availableWidth={1200}
                      onClipDrag={handleShapeDrag}
                      onClipResize={handleShapeResize}
                      onClipSplit={() => {}} // Shapes don't split
                      selectedClipId={selectedShapeId}
                      onClipSelect={(shapeId) => {
                        // Always select, never deselect when clicking on a shape
                        setSelectedShapeId(shapeId);
                        // Also clear other selections
                        setSelectedClipId(null);
                        setSelectedTextId(null);
                      }}
                      onClipRightClick={handleLayerRightClick}
                      trackIndex={textLayers.length + index} // Pass index for positioning after text tracks
                    />
                  ))}
                  
                  
                  {/* Video track */}
                  <TimelineTrack
                    trackType="video"
                    clips={videoClips}
                    duration={duration}
                    availableWidth={1200}
                    onClipDrag={handleClipDrag}
                    onClipResize={handleClipResize}
                    onClipSplit={handleClipSplit}
                    selectedClipId={selectedClipId}
                    onClipSelect={(clipId) => {
                      // Always select, never deselect when clicking on a clip
                      setSelectedClipId(clipId);
                      // Also clear other selections
                      setSelectedTextId(null);
                      setSelectedShapeId(null);
                    }}
                    videoTrackOffset={textLayers.length + shapeLayers.length} // Offset by number of text and shape tracks
                    onRippleDelete={handleRippleDeleteRequest}
                  />
                  
                  {/* Audio track */}
                  <TimelineTrack
                    trackType="audio"
                    clips={audioClips}
                    duration={duration}
                    availableWidth={1200}
                    onClipDrag={handleClipDrag}
                    onClipResize={handleClipResize}
                    onClipSplit={handleClipSplit}
                    selectedClipId={selectedClipId}
                    onClipSelect={(clipId) => {
                      // Always select, never deselect when clicking on a clip
                      setSelectedClipId(clipId);
                      // Also clear other selections
                      setSelectedTextId(null);
                      setSelectedShapeId(null);
                    }}
                    waveformData={waveformData}
                    audioTrackOffset={textLayers.length + shapeLayers.length} // Offset by number of text and shape tracks
                    onRippleDelete={handleRippleDeleteRequest}
                  />
                  
                  {/* Playhead line extending through all tracks */}
                  <Playhead 
                    currentTime={currentTime} 
                    duration={duration} 
                    availableWidth={1200} 
                    height={Math.max(180, ((textLayers.length + shapeLayers.length) * 70) + 140)}
                    lineOnly={true}
                  />
                </Layer>
              </Stage>
            </div>
          </div>

          {/* Layer Context Menu - positioned relative to timeline container */}
          <LayerContextMenu
            isVisible={layerContextMenu.isVisible}
            x={layerContextMenu.x}
            y={layerContextMenu.y}
            layerType={layerContextMenu.layerType}
            onExtendToStart={handleExtendToStart}
            onExtendToEnd={handleExtendToEnd}
            onDuplicate={handleDuplicateLayer}
            onClose={handleCloseLayerContextMenu}
          />
        </div>

      </main>

      {/* Script Editing Modal */}
      {showScriptModal && (
        <div className="modal-overlay" onClick={() => setShowScriptModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Script - {currentLanguage === 'original' ? 'Original' : currentLanguage.toUpperCase()}</h3>
              <button className="modal-close" onClick={() => setShowScriptModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
                  Describe What's Happening
                </label>
                <textarea
                  value={scriptDescription}
                  onChange={(e) => setScriptDescription(e.target.value)}
                  placeholder="Describe the scene or action taking place..."
                  style={{
                    width: '100%',
                    height: '80px',
                    padding: '10px',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    backgroundColor: '#1a1a1a',
                    color: '#fff',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
                  Scene Narration
                </label>
                <textarea
                  value={scriptNarration}
                  onChange={(e) => setScriptNarration(e.target.value)}
                  placeholder="Enter the narration text for this scene..."
                  style={{
                    width: '100%',
                    height: '120px',
                    padding: '10px',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    backgroundColor: '#1a1a1a',
                    color: '#fff',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button cancel" 
                onClick={() => setShowScriptModal(false)}
                disabled={isGeneratingAudio}
              >
                Cancel
              </button>
              <button 
                className="modal-button primary" 
                onClick={saveScriptChanges}
                disabled={isGeneratingAudio || isRegeneratingScript}
              >
                Save Changes
              </button>
              <button 
                className="modal-button" 
                onClick={regenerateScript}
                disabled={isRegeneratingScript || isGeneratingAudio || !scriptDescription.trim()}
                style={{ 
                  backgroundColor: isRegeneratingScript ? '#666' : '#28a745',
                  marginLeft: '10px'
                }}
              >
                {isRegeneratingScript ? 'Regenerating...' : 'Re-gen Script'}
              </button>
              <button 
                className="modal-button" 
                onClick={regenerateAudio}
                disabled={isGeneratingAudio || isRegeneratingScript || !scriptNarration.trim()}
                style={{ 
                  backgroundColor: isGeneratingAudio ? '#666' : '#007bff',
                  marginLeft: '10px'
                }}
              >
                {isGeneratingAudio ? 'Generating...' : 'Regenerate Audio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paste Shape Modal */}
      {showPasteModal && (
        <div className="modal-overlay" onClick={() => setShowPasteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Paste Shape Layer</h3>
              <button className="modal-close" onClick={() => setShowPasteModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
                  Shape Layer JSON
                </label>
                <textarea
                  value={pasteJson}
                  onChange={(e) => setPasteJson(e.target.value)}
                  placeholder='Paste the JSON properties of the shape layer here, e.g.:
{
  "shapeType": "rectangle",
  "x": 100,
  "y": 100,
  "width": 200,
  "height": 100,
  "fill": "#ff0000",
  "stroke": "transparent",
  "strokeWidth": 0,
  "mode": "fill"
}'
                  style={{
                    width: '100%',
                    height: '300px',
                    padding: '12px',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    backgroundColor: '#1a1a1a',
                    color: '#fff',
                    fontSize: '13px',
                    resize: 'vertical',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '15px' }}>
                <strong>Tip:</strong> Copy a shape using the copy button, then paste the JSON here to duplicate it.
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button cancel" 
                onClick={() => {
                  setShowPasteModal(false);
                  setPasteJson('');
                }}
              >
                Cancel
              </button>
              <button 
                className="modal-button primary" 
                onClick={() => {
                  const success = handlePasteShape(pasteJson);
                  if (success) {
                    setShowPasteModal(false);
                    setPasteJson('');
                  } else {
                    alert('Invalid JSON format. Please check your input and try again.');
                  }
                }}
                disabled={!pasteJson.trim()}
              >
                Paste Shape
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="modal-overlay" onClick={cancelReset}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Reset</h3>
              <button className="modal-close" onClick={cancelReset}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reset the timeline configuration?</p>
              <p style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
                This will permanently delete all custom edits for <strong>{currentLanguage === 'original' ? 'the original language' : currentLanguage.toUpperCase()}</strong> and cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={cancelReset}>
                Cancel
              </button>
              <button className="modal-button confirm" onClick={resetTimeline}>
                Reset Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Generation Modal */}
      <VideoGenerationModal
        isOpen={isVideoGenerationModalOpen}
        onClose={handleCloseVideoGenerationModal}
        isGenerating={isGeneratingVideo}
        progress={videoGenerationProgress}
        result={videoGenerationResult}
        error={videoGenerationError}
      />

      {/* Ripple Delete Context Menu */}
      <RippleDeleteContextMenu
        isVisible={contextMenu.isVisible}
        x={contextMenu.x}
        y={contextMenu.y}
        onRippleDelete={handleRippleDelete}
        onClose={handleCloseContextMenu}
        gapDuration={contextMenu.gapDuration}
        clipsCount={contextMenu.clipsToMove.length}
      />

    </div>
  );
}

export default ClipEditorPage;